import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert, Modal, Tabs, Tab, Badge } from 'react-bootstrap';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import GroupOptionsManagement from '../components/GroupOptionsManagement';
import CategoryManagement from '../components/CategoryManagement';
import CopyItemsSelector from '../components/CopyItemsSelector';

// CSS стили для горизонтальной прокрутки категорий и вертикальной прокрутки меню
const categoryScrollStyles = `
  .category-scroll-container {
    scrollbar-width: thin;
    scrollbar-color: #6c757d #f8f9fa;
    cursor: grab;
    user-select: none;
  }
  
  .category-scroll-container:active {
    cursor: grabbing;
  }
  
  .category-scroll-container::-webkit-scrollbar {
    height: 6px;
  }
  
  .category-scroll-container::-webkit-scrollbar-track {
    background: #f8f9fa;
    border-radius: 3px;
  }
  
  .category-scroll-container::-webkit-scrollbar-thumb {
    background: #6c757d;
    border-radius: 3px;
  }
  
  .category-scroll-container::-webkit-scrollbar-thumb:hover {
    background: #495057;
  }

  .menu-items-scroll-container {
    scrollbar-width: thin;
    scrollbar-color: #6c757d #f8f9fa;
  }
  
  .menu-items-scroll-container::-webkit-scrollbar {
    width: 8px;
  }
  
  .menu-items-scroll-container::-webkit-scrollbar-track {
    background: #f8f9fa;
    border-radius: 4px;
  }
  
  .menu-items-scroll-container::-webkit-scrollbar-thumb {
    background: #6c757d;
    border-radius: 4px;
  }
  
  .menu-items-scroll-container::-webkit-scrollbar-thumb:hover {
    background: #495057;
  }
`;

const MenuManagement = () => {
  const [restaurant, setRestaurant] = useState(null);
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [canEdit, setCanEdit] = useState(true);
  const [addonsExpanded, setAddonsExpanded] = useState(false);
  const [groupOptionsExpanded, setGroupOptionsExpanded] = useState(false);
  const [editAddonsExpanded, setEditAddonsExpanded] = useState(false);
  const [editGroupOptionsExpanded, setEditGroupOptionsExpanded] = useState(false);
  
  // Form state for adding new items
  const [newItem, setNewItem] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    image: '',
    weight: '',
    weightUnit: 'г',
    status: 'active',
    isAvailable: true,
    addons: [],
    addonGroups: [],
    groupOptionIds: [],
    });
  
    // Image upload state
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');

    // Collapsible sections state
    const [expandedSections, setExpandedSections] = useState({
      contact: false,
      social: false,
      workingHours: false,
      theme: false,
      backgroundImage: false,
      slideshow: false,
      defaultMenu: false,
      restaurantIcons: false,
      preview: true // Предпросмотр открыт по умолчанию
    });

    // Toggle section function
    const toggleSection = (section) => {
      setExpandedSections(prev => ({
        ...prev,
        [section]: !prev[section]
      }));
    };

    // Branding form state
    const [brandingForm, setBrandingForm] = useState({
      logoUrl: '',
      footerText: '',
      adText: '',
      contact: { phone: '', email: '', address: '', website: '' },
      social: { instagram: '', facebook: '', telegram: '', vk: '', whatsapp: '' },
      workingHours: {
        monday: { open: '', close: '', closed: false },
        tuesday: { open: '', close: '', closed: false },
        wednesday: { open: '', close: '', closed: false },
        thursday: { open: '', close: '', closed: false },
        friday: { open: '', close: '', closed: false },
        saturday: { open: '', close: '', closed: false },
        sunday: { open: '', close: '', closed: false },
        timezone: 'Europe/Minsk',
        restrictOrdersOutsideHours: false
      },
      theme: {
        primary: '#007bff',
        secondary: '#6c757d',
        background: '#ffffff',
        surface: '#ffffff',
        text: '#212529',
        border: '#dee2e6',
        success: '#28a745',
        error: '#dc3545',
        opacity: 0.9
      },
      backgroundImage: {
        url: '',
        opacity: 0.1,
        position: 'center',
        size: 'cover',
        repeat: 'no-repeat'
      },
      slideshow: {
        enabled: false,
        images: [],
        autoplay: true,
        interval: 3000,
        showIndicators: true,
        showArrows: true
      }
    });
    const [logoUploading, setLogoUploading] = useState(false);
    const [logoError, setLogoError] = useState('');
    // Per-item status map
    const [itemStatusMap, setItemStatusMap] = useState({});
    // Inline edit map for items
    const [editItemMap, setEditItemMap] = useState({});
    // Menu mode selector: dine_in | delivery
    const [menuMode, setMenuMode] = useState('dine_in');
  
// Multi-menu management state
  const [menus, setMenus] = useState([]);
  const [activeMenuId, setActiveMenuId] = useState('');
  const [newMenuTitle, setNewMenuTitle] = useState('');
  const [newMenuMode, setNewMenuMode] = useState('dine_in');
  const [newMenuExpressPayToken, setNewMenuExpressPayToken] = useState('');
  const [newMenuExpressPaySecret, setNewMenuExpressPaySecret] = useState('');
  const [renameMap, setRenameMap] = useState({});
  
  // Copy menu items state
  const [copyFromMenuId, setCopyFromMenuId] = useState('');
  const [showCopyOptions, setShowCopyOptions] = useState(false);
  const [copyAllItems, setCopyAllItems] = useState(false);
  const [selectedItemsToCopy, setSelectedItemsToCopy] = useState([]);
  const [copyCategories, setCopyCategories] = useState(true);
  
  // Group options state
  const [groupOptions, setGroupOptions] = useState([]);
  
  // Default menu settings for subdomain
  const [defaultMenuId, setDefaultMenuId] = useState('');
  const [savingDefaultMenu, setSavingDefaultMenu] = useState(false);
 
  // When switching active menu, clear selection/edits so categories match the chosen menu
  useEffect(() => {
    setNewItem(prev => ({ ...prev, category: '' }));
  }, [activeMenuId]);

  // Sync branding form from loaded menu
  useEffect(() => {
    if (menu && menu.branding) {
      setBrandingForm({
        logoUrl: menu.branding.logoUrl || '',
        footerText: menu.branding.footerText || '',
        adText: menu.branding.adText || '',
        contact: menu.branding.contact || {
          phone: '',
          email: '',
          address: '',
          website: '',
        },
        social: menu.branding.social || {
          instagram: '',
          facebook: '',
          telegram: '',
          vk: '',
          whatsapp: '',
        },
        workingHours: {
          monday: { open: '', close: '', closed: false },
          tuesday: { open: '', close: '', closed: false },
          wednesday: { open: '', close: '', closed: false },
          thursday: { open: '', close: '', closed: false },
          friday: { open: '', close: '', closed: false },
          saturday: { open: '', close: '', closed: false },
          sunday: { open: '', close: '', closed: false },
          timezone: 'Europe/Minsk',
          restrictOrdersOutsideHours: false,
          ...menu.branding.workingHours
        },
        theme: menu.branding.theme || {
          primary: '#007bff',
          secondary: '#6c757d',
          background: '#ffffff',
          surface: '#ffffff',
          text: '#212529',
          border: '#dee2e6',
          success: '#28a745',
          error: '#dc3545',
          opacity: 0.9
        },
        backgroundImage: menu.branding.backgroundImage || {
          url: '',
          opacity: 0.1,
          position: 'center',
          size: 'cover',
          repeat: 'no-repeat'
        },
        slideshow: menu.branding.slideshow || {
          enabled: false,
          images: [],
          autoplay: true,
          interval: 3000,
          showIndicators: true,
          showArrows: true
        }
      });
    } else {
      setBrandingForm({
        logoUrl: '',
        footerText: '',
        adText: '',
        contact: { phone: '', email: '', address: '', website: '' },
        social: { instagram: '', facebook: '', telegram: '', vk: '', whatsapp: '' },
        workingHours: {
          monday: { open: '', close: '', closed: false },
          tuesday: { open: '', close: '', closed: false },
          wednesday: { open: '', close: '', closed: false },
          thursday: { open: '', close: '', closed: false },
          friday: { open: '', close: '', closed: false },
          saturday: { open: '', close: '', closed: false },
          sunday: { open: '', close: '', closed: false },
          timezone: 'Europe/Minsk',
          restrictOrdersOutsideHours: false
        },
        theme: {
          primary: '#007bff',
          secondary: '#6c757d',
          background: '#ffffff',
          surface: '#ffffff',
          text: '#212529',
          border: '#dee2e6',
          success: '#28a745',
          error: '#dc3545',
          opacity: 0.9
        },
        backgroundImage: {
          url: '',
          opacity: 0.1,
          position: 'center',
          size: 'cover',
          repeat: 'no-repeat'
        },
        slideshow: {
          enabled: false,
          images: [],
          autoplay: true,
          interval: 3000,
          showIndicators: true,
          showArrows: true
        },
        deliverySettings: {
          enabled: false,
          minOrderAmount: 0,
          deliveryFee: 0
        }
      });
    }
  }, [menu]);
 
  // QR modal state
  const [qrVisible, setQrVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [restaurantBranding, setRestaurantBranding] = useState({
    faviconUrl: '',
    appleTouchIconUrl: '',
    pwaIcon192Url: '',
    pwaIcon512Url: '',
    pageTitle: ''
  });
  const [savingRestaurantBranding, setSavingRestaurantBranding] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [activeCategory, setActiveCategory] = useState('');
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [dishSearchTerm, setDishSearchTerm] = useState('');
  const [qrUrl, setQrUrl] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, scrollLeft: 0 });

  // Обработчики для drag-to-scroll
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.pageX,
      scrollLeft: e.currentTarget.scrollLeft
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX;
    const walk = (x - dragStart.x) * 2; // Скорость прокрутки
    e.currentTarget.scrollLeft = dragStart.scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Предотвращаем клик по кнопкам во время перетаскивания
  const handleButtonClick = (e, action) => {
    if (isDragging) {
      e.preventDefault();
      return;
    }
    action();
  };

  // Мемоизированное группирование блюд по категориям с фильтрацией
  const itemsByCategory = useMemo(() => {
    if (!menu?.items) return {};
    
    let filteredItems = menu.items;
    
    // Фильтрация по названию блюда
    if (dishSearchTerm) {
      filteredItems = menu.items.filter(item => 
        item.name.toLowerCase().includes(dishSearchTerm.toLowerCase()) ||
        (item.description && item.description.toLowerCase().includes(dishSearchTerm.toLowerCase()))
      );
    }
    
    const grouped = {};
    filteredItems.forEach(item => {
      const category = item.category || 'Без категории';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(item);
    });
    return grouped;
  }, [menu?.items, dishSearchTerm]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const qrCanvasId = 'menu-qr-canvas';
  const qrRef = useRef(null);

  // Branding modal state
  const [brandingVisible, setBrandingVisible] = useState(false);

const buildMenuUrl = (menuId) => {
    // Проверяем, находимся ли мы на субдомене
    const isSubdomain = window.location.hostname.includes('.menu.qr-code.by') && 
                       !window.location.hostname.startsWith('menu.qr-code.by');
    
    if (isSubdomain) {
      // На субдомене - всегда используем текущий субдомен с menuId
      return `https://${window.location.hostname}?menuId=${menuId}`;
    } else if (restaurant?.subdomain) {
      // На основном домене, но у ресторана есть субдомен - используем субдомен
      return `https://${restaurant.subdomain}.menu.qr-code.by?menuId=${menuId}`;
    } else {
      // На основном домене без субдомена - используем обычный URL
      const origin = window.location.origin;
      return `${origin}/menu/${restaurant?._id || ''}?menuId=${menuId}`;
    }
  };

  const openQR = (menuId) => {
    const url = buildMenuUrl(menuId);
    setQrUrl(url);
    setQrVisible(true);
  };

  const downloadQR = () => {
    try {
      const canvas = document.getElementById(qrCanvasId);
      if (!canvas) return;
      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = 'menu-qr.png';
      link.click();
    } catch (e) {
      console.error(e);
    }
  };

  const printQR = () => {
    try {
      const canvas = document.getElementById(qrCanvasId);
      if (!canvas) return;
      const dataUrl = canvas.toDataURL('image/png');
      const w = window.open('', '_blank');
      if (!w) return;
      const html = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>QR</title>
    <style>
      body { margin: 0; padding: 24px; text-align: center; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji'; }
      img { width: 300px; height: 300px; }
      .url { margin-top: 10px; word-break: break-all; font-size: 12px; color: #333; }
      @media print { .url { font-size: 10px; } }
    </style>
  </head>
  <body>
    <img id="qrImg" src="${dataUrl}" alt="QR" />
    <div class="url">${qrUrl}</div>
    <script>
      const img = document.getElementById('qrImg');
      function safePrint(){
        try { window.focus(); window.print(); } catch(e) {}
        try { window.close(); } catch(e) {}
      }
      if (img && !img.complete) {
        img.onload = function(){ setTimeout(safePrint, 100); };
        img.onerror = function(){ setTimeout(safePrint, 100); };
      } else {
        setTimeout(safePrint, 100);
      }
    </script>
  </body>
</html>`;
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (e) {
      console.error(e);
    }
  };

  const copyQRUrl = async () => {
    try {
      await navigator.clipboard.writeText(qrUrl);
      setSuccess('Ссылка QR скопирована в буфер обмена');
    } catch (e) {
      console.error(e);
    }
  };

  // Load group options for the restaurant
  const fetchGroupOptions = async (restaurantId) => {
    try {
      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('managerToken');
      if (!token || !restaurantId) return;
      
      const response = await axios.get(`/api/group-options/${restaurantId}`, {
        headers: { 'x-auth-token': token }
      });
      setGroupOptions(response.data);
    } catch (error) {
      console.error('Error fetching group options:', error);
    }
  };

  // Handle menu update from CategoryManagement
  const handleMenuUpdate = (updatedMenu) => {
    setMenu(updatedMenu);
  };

  // Load list of menus for this restaurant (owner/manager)
  const loadMenus = async (restaurantId) => {
    try {
      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('managerToken');
      if (!token) return;
      const res = await axios.get(`/api/menu/${restaurantId}/list`, {
        headers: { 'x-auth-token': token }
      });
      const data = Array.isArray(res.data) ? res.data : [];
      setMenus(data);
      // Keep active selection if exists, otherwise try to select first
      if (data.length > 0) {
        if (!activeMenuId || !data.some(m => m._id === activeMenuId)) {
          setActiveMenuId(data[0]._id);
        }
      } else {
        setActiveMenuId('');
      }
      return data;
    } catch (err) {
      // list is protected; ignore error visually but log it
      console.error('Error loading menus list:', err);
      return [];
    }
  };

  // Select active menu by id and refresh menu content
  const handleSelectMenu = async (menuId) => {
    try {
      setActiveMenuId(menuId);
      if (restaurant?._id) {
        await fetchMenu(restaurant._id, { menuId });
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Create a new empty menu for this restaurant
  const handleCreateMenu = async () => {
    try {
      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('managerToken');
      if (!token) {
        setError('Токен не найден. Пожалуйста, войдите снова.');
        return;
      }
      if (!canEdit) {
        setError('Недостаточно прав для управления меню');
        return;
      }
      if (!newMenuTitle.trim()) {
        setError('Укажите название нового меню');
        return;
      }
      const res = await axios.post(
        `/api/menu/${restaurant._id}/new`,
        { 
          title: newMenuTitle.trim(), 
          mode: newMenuMode, 
          expressPayToken: newMenuExpressPayToken.trim() || undefined, 
          expressPaySecret: newMenuExpressPaySecret.trim() || undefined,
          paymentOptions: newMenuMode === 'delivery'
            ? { cash: true, expresspay: true }
            : undefined
        },
        { headers: { 'x-auth-token': token } }
      );
      setSuccess('Меню создано');
      setError('');
      setNewMenuTitle('');
      setNewMenuExpressPayToken('');
      setNewMenuExpressPaySecret('');
      // reload list and select newly created
      const list = await loadMenus(restaurant._id);
      setActiveMenuId(res.data._id);
      await fetchMenu(restaurant._id, { menuId: res.data._id });
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось создать меню');
      setSuccess('');
    }
  };

  const setRenameField = (menuId, field, value) => {
    setRenameMap((prev) => ({
      ...prev,
      [menuId]: { ...(prev[menuId] || {}), [field]: value }
    }));
  };

  // Rename/change mode for an existing menu
  const handleRenameMenu = async (menuId) => {
    try {
      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('managerToken');
      if (!token) {
        setError('Токен не найден. Пожалуйста, войдите снова.');
        return;
      }
      if (!canEdit) {
        setError('Недостаточно прав для управления меню');
        return;
      }
      const payload = {};
      const edit = renameMap[menuId] || {};
      if (typeof edit.title === 'string') payload.title = edit.title;
      if (typeof edit.mode === 'string') payload.mode = edit.mode;
      if (typeof edit.expressPayToken === 'string') payload.expressPayToken = edit.expressPayToken;
      if (typeof edit.expressPaySecret === 'string') payload.expressPaySecret = edit.expressPaySecret;
      if (edit.paymentOptions && typeof edit.paymentOptions === 'object') payload.paymentOptions = edit.paymentOptions;
      const res = await axios.put(
        `/api/menu/${restaurant._id}/menus/${menuId}`,
        payload,
        { headers: { 'x-auth-token': token } }
      );
      setSuccess('Меню обновлено');
      setError('');
      await loadMenus(restaurant._id);
      // if mode/name changed and this is active, refresh
      if (menuId === activeMenuId) {
        await fetchMenu(restaurant._id);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось обновить меню');
      setSuccess('');
    }
  };

  // Copy items from another menu
  const handleCopyItems = async () => {
    try {
      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('managerToken');
      if (!token) {
        setError('Токен не найден. Пожалуйста, войдите снова.');
        return;
      }
      if (!canEdit) {
        setError('Недостаточно прав для управления меню');
        return;
      }
      if (!copyFromMenuId) {
        setError('Выберите меню для копирования');
        return;
      }
      if (!activeMenuId) {
        setError('Выберите целевое меню');
        return;
      }
      if (!copyAllItems && selectedItemsToCopy.length === 0) {
        setError('Выберите блюда для копирования');
        return;
      }

      const payload = {
        copyFromMenuId,
        copyAllItems,
        selectedItemsToCopy: copyAllItems ? [] : selectedItemsToCopy,
        copyCategories
      };

      const res = await axios.post(
        `/api/menu/${restaurant._id}/menus/${activeMenuId}/copy-items`,
        payload,
        { headers: { 'x-auth-token': token } }
      );

      setSuccess(`Скопировано ${res.data.copiedItemsCount} блюд и ${res.data.copiedCategoriesCount} категорий`);
      setError('');
      
      // Reset copy state
      setCopyFromMenuId('');
      setShowCopyOptions(false);
      setCopyAllItems(false);
      setSelectedItemsToCopy([]);
      setCopyCategories(true);
      
      // Refresh current menu
      await fetchMenu(restaurant._id, { menuId: activeMenuId });
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось скопировать блюда');
      setSuccess('');
    }
  };

  // Get items from a specific menu for copying
  const getMenuItems = async (menuId) => {
    try {
      const res = await axios.get(`/api/menu/by-id/${menuId}`);
      return res.data.items || [];
    } catch (err) {
      console.error('Error fetching menu items:', err);
      return [];
    }
  };

  // Delete a menu by id
  const handleDeleteMenu = async (menuId) => {
    try {
      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('managerToken');
      if (!token) {
        setError('Токен не найден. Пожалуйста, войдите снова.');
        return;
      }
      if (!canEdit) {
        setError('Недостаточно прав для управления меню');
        return;
      }
      await axios.delete(
        `/api/menu/${restaurant._id}/menus/${menuId}`,
        { headers: { 'x-auth-token': token } }
      );
      setSuccess('Меню удалено');
      setError('');
      const list = await loadMenus(restaurant._id);
      // If deleted the active menu, pick the next available one; otherwise keep current
      let nextId = activeMenuId;
      if (activeMenuId === menuId) {
        nextId = list && list[0]?._id;
        setActiveMenuId(nextId || '');
      }
      // If deleted menu was default, clear default
      if (defaultMenuId === menuId) {
        setDefaultMenuId('');
      }
      await fetchMenu(restaurant._id, { menuId: nextId });
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось удалить меню');
      setSuccess('');
    }
  };

  // Save default menu settings
  const handleSaveDefaultMenu = async () => {
    try {
      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('managerToken');
      if (!token) {
        setError('Токен не найден. Пожалуйста, войдите снова.');
        return;
      }
      if (!canEdit) {
        setError('Недостаточно прав для управления меню');
        return;
      }
      
      setSavingDefaultMenu(true);
      setError('');
      
      await axios.put('/api/restaurant/default-menu', {
        defaultMenuId: defaultMenuId || null
      }, {
        headers: { 'x-auth-token': token }
      });
      
      setSuccess('Настройки меню по умолчанию сохранены');
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось сохранить настройки меню по умолчанию');
      setSuccess('');
    } finally {
      setSavingDefaultMenu(false);
    }
  };
  useEffect(() => {
    const fetchRestaurantProfile = async () => {
      try {
        const ownerToken = localStorage.getItem('restaurantToken');
        const managerToken = localStorage.getItem('managerToken');
        const token = ownerToken || managerToken;
        
        // Check if token exists
        if (!token) {
          setError('Токен не найден. Пожалуйста, войдите снова.');
          setLoading(false);
          return;
        }
        
        if (ownerToken) {
          const res = await axios.get('/api/auth/profile', {
            headers: { 'x-auth-token': ownerToken }
          });
          setRestaurant(res.data);
          setCanEdit(true);
          
          // Load default menu settings and restaurant branding
          try {
            const restaurantRes = await axios.get('/api/restaurant/me', {
              headers: { 'x-auth-token': ownerToken }
            });
            const defaultMenu = restaurantRes.data.defaultMenuId;
            if (defaultMenu) {
              // Handle both populated object and string ID
              setDefaultMenuId(typeof defaultMenu === 'object' ? defaultMenu._id : defaultMenu);
            } else {
              setDefaultMenuId('');
            }
            // Load restaurant branding settings
            if (restaurantRes.data.branding) {
              setRestaurantBranding({
                faviconUrl: restaurantRes.data.branding.faviconUrl || '',
                appleTouchIconUrl: restaurantRes.data.branding.appleTouchIconUrl || '',
                pwaIcon192Url: restaurantRes.data.branding.pwaIcon192Url || '',
                pwaIcon512Url: restaurantRes.data.branding.pwaIcon512Url || '',
                pageTitle: restaurantRes.data.branding.pageTitle || ''
              });
            }
          } catch (err) {
            console.error('Error loading default menu settings:', err);
          }
          
          // Fetch menus and pick a concrete menu ID to load immediately
          const list = await loadMenus(res.data._id);
          const chosenId = (activeMenuId && list?.some(m => m._id === activeMenuId)) ? activeMenuId : (list && list[0]?._id);
          if (chosenId) {
            setActiveMenuId(chosenId);
            await fetchMenu(res.data._id, { menuId: chosenId });
          } else {
            setActiveMenuId('');
            await fetchMenu(res.data._id, {});
          }
          // Fetch group options
          await fetchGroupOptions(res.data._id);
        } else {
          // Manager profile
          const res = await axios.get('/api/manager/profile', {
            headers: { 'x-auth-token': managerToken }
          });
          // Build minimal restaurant object for header
          setRestaurant({
            _id: res.data.restaurantId,
            restaurantName: res.data.restaurantName,
            subdomain: res.data.subdomain
          });
          setCanEdit(!!res.data.canManageMenu);
          
          // Load default menu settings and restaurant branding (only if canManageMenu)
          if (res.data.canManageMenu) {
            try {
              const restaurantRes = await axios.get('/api/restaurant/me', {
                headers: { 'x-auth-token': managerToken }
              });
              const defaultMenu = restaurantRes.data.defaultMenuId;
              if (defaultMenu) {
                // Handle both populated object and string ID
                setDefaultMenuId(typeof defaultMenu === 'object' ? defaultMenu._id : defaultMenu);
              } else {
                setDefaultMenuId('');
              }
              // Load restaurant branding settings
              if (restaurantRes.data.branding) {
                setRestaurantBranding({
                  faviconUrl: restaurantRes.data.branding.faviconUrl || '',
                  appleTouchIconUrl: restaurantRes.data.branding.appleTouchIconUrl || '',
                  pwaIcon192Url: restaurantRes.data.branding.pwaIcon192Url || '',
                  pwaIcon512Url: restaurantRes.data.branding.pwaIcon512Url || '',
                  pageTitle: restaurantRes.data.branding.pageTitle || ''
                });
              }
            } catch (err) {
              console.error('Error loading default menu settings:', err);
            }
          }
          
          const list = await loadMenus(res.data.restaurantId);
          const chosenId = (activeMenuId && list?.some(m => m._id === activeMenuId)) ? activeMenuId : (list && list[0]?._id);
          if (chosenId) {
            setActiveMenuId(chosenId);
            await fetchMenu(res.data.restaurantId, { menuId: chosenId });
          } else {
            setActiveMenuId('');
            await fetchMenu(res.data.restaurantId, {});
          }
          // Fetch group options
          await fetchGroupOptions(res.data.restaurantId);
        }
      } catch (err) {
        console.error('Error fetching restaurant profile:', err);
        if (err.response) {
          // Server responded with error status
          setError(`Ошибка сервера: ${err.response.data.message || err.response.statusText}`);
        } else if (err.request) {
          // Request was made but no response received
          setError('Ошибка сети: Не удалось получить ответ от сервера');
        } else {
          // Something else happened
          setError(`Ошибка: ${err.message}`);
        }
        setLoading(false);
      }
    };
    
    fetchRestaurantProfile();
  }, []);
  
  const fetchMenu = async (restaurantId, opts = {}) => {
    try {
      let res;
      const idToUse = opts.menuId || activeMenuId;
      if (idToUse) {
        res = await axios.get(`/api/menu/by-id/${idToUse}`);
      } else {
        res = await axios.get(`/api/menu/${restaurantId}?mode=${menuMode}`);
      }
      
      setMenu(res.data);
      // sync item status map from fetched menu to avoid stale states on switch
      const map = {};
      (res.data.items || []).forEach(it => {
        map[it._id] = it.status || (it.isAvailable ? 'active' : 'hidden');
      });
      setItemStatusMap(map);
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 404) {
        setMenu(null);
        setItemStatusMap({});
        setLoading(false);
      } else {
        console.error('Error fetching menu:', err);
        if (err.response) {
          setError(`Ошибка сервера: ${err.response.data.message || err.response.statusText}`);
        } else if (err.request) {
          setError('Ошибка сети: Не удалось получить ответ от сервера');
        } else {
          setError(`Ошибка: ${err.message}`);
        }
        setLoading(false);
      }
    }
  };

  // Upload image handler with mobile support
  const handleUploadImage = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    // Validate file size (50MB limit)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      setUploadError('Файл слишком большой. Максимальный размер: 50MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Пожалуйста, выберите файл изображения');
      return;
    }

    setUploadError('');
    setUploading(true);

    try {
      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('managerToken');
      if (!token) {
        setError('Токен не найден. Пожалуйста, войдите снова.');
        setUploading(false);
        return;
      }
      if (!canEdit) {
        setUploadError('Недостаточно прав для загрузки изображений');
        setUploading(false);
        return;
      }

      // For mobile devices, compress image if it's too large
      let processedFile = file;
      if (file.size > 5 * 1024 * 1024) { // If larger than 5MB, compress
        processedFile = await compressImage(file);
      }

      const formData = new FormData();
      formData.append('image', processedFile);

      const res = await axios.post('/api/upload/image', formData, {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000 // 60 seconds timeout for large files
      });

      setNewItem((prev) => ({ ...prev, image: res.data.url }));
      setSuccess('Изображение загружено');
    } catch (err) {
      console.error('Upload error:', err);
      let message = 'Ошибка загрузки файла';
      
      if (err.code === 'ECONNABORTED') {
        message = 'Время загрузки истекло. Попробуйте уменьшить размер изображения';
      } else if (err.response?.status === 413) {
        message = 'Файл слишком большой. Максимальный размер: 50MB';
      } else if (err.response?.status === 400) {
        message = err.response.data?.message || 'Неверный формат файла';
      } else if (err.response?.data?.message) {
        message = err.response.data.message;
      }
      
      setUploadError(message);
    } finally {
      setUploading(false);
    }
  };

  // Compress image for mobile devices
  const compressImage = (file) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions (max 1920px width)
        const maxWidth = 1920;
        const maxHeight = 1080;
        let { width, height } = img;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/jpeg', 0.8); // 80% quality
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  // Resize image to specific size using canvas
  const resizeImage = (file, targetWidth, targetHeight, quality = 0.9) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Set canvas size
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        
        // Draw image with high quality scaling
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
        
        // Convert to blob (PNG for icons to preserve transparency)
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob'));
          }
        }, 'image/png', quality);
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  };

  // Upload master icon and automatically generate all sizes
  const handleUploadMasterIcon = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    
    setSavingRestaurantBranding(true);
    setError('');
    
    try {
      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('managerToken');
      if (!token) {
        setError('Токен не найден. Пожалуйста, войдите снова.');
        setSavingRestaurantBranding(false);
        return;
      }
      if (!canEdit) {
        setError('Недостаточно прав для загрузки иконки');
        setSavingRestaurantBranding(false);
        return;
      }

      setSuccess('Создание иконок всех размеров...');

      // Define all icon sizes we need
      const iconSizes = [
        { size: 32, type: 'faviconUrl', name: 'Favicon' },
        { size: 180, type: 'appleTouchIconUrl', name: 'Apple Touch Icon' },
        { size: 192, type: 'pwaIcon192Url', name: 'PWA Icon 192' },
        { size: 512, type: 'pwaIcon512Url', name: 'PWA Icon 512' }
      ];

      // Create all sizes and upload them
      const uploadPromises = iconSizes.map(async ({ size, type, name }) => {
        try {
          // Resize image
          const resizedBlob = await resizeImage(file, size, size, 0.95);
          
          // Create FormData and upload
          const formData = new FormData();
          formData.append('image', resizedBlob, `${name}-${size}x${size}.png`);
          
          const res = await axios.post('/api/upload/image', formData, {
            headers: { 'x-auth-token': token, 'Content-Type': 'multipart/form-data' }
          });
          
          return { type, url: res.data.url };
        } catch (err) {
          console.error(`Error uploading ${name}:`, err);
          throw new Error(`Ошибка загрузки ${name}: ${err.message}`);
        }
      });

      // Wait for all uploads to complete
      const results = await Promise.all(uploadPromises);
      
      // Update state with all URLs
      const updatedBranding = { ...restaurantBranding };
      results.forEach(({ type, url }) => {
        updatedBranding[type] = url;
      });
      
      setRestaurantBranding(updatedBranding);
      setSuccess('Все иконки успешно созданы и загружены!');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Ошибка загрузки иконок');
    } finally {
      setSavingRestaurantBranding(false);
    }
  };

  // Upload icon for restaurant branding (individual)
  const handleUploadRestaurantIcon = async (e, iconType) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setSavingRestaurantBranding(true);
    try {
      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('managerToken');
      if (!token) {
        setError('Токен не найден. Пожалуйста, войдите снова.');
        setSavingRestaurantBranding(false);
        return;
      }
      if (!canEdit) {
        setError('Недостаточно прав для загрузки иконки');
        setSavingRestaurantBranding(false);
        return;
      }
      const formData = new FormData();
      formData.append('image', file);
      const res = await axios.post('/api/upload/image', formData, {
        headers: { 'x-auth-token': token, 'Content-Type': 'multipart/form-data' }
      });
      setRestaurantBranding((prev) => ({ ...prev, [iconType]: res.data.url }));
      setSuccess('Иконка загружена');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Ошибка загрузки иконки');
    } finally {
      setSavingRestaurantBranding(false);
    }
  };

  // Save restaurant branding (icons and title)
  const handleSaveRestaurantBranding = async () => {
    try {
      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('managerToken');
      if (!token) {
        setError('Токен не найден. Пожалуйста, войдите снова.');
        return;
      }
      if (!canEdit) {
        setError('Недостаточно прав для изменения настроек');
        return;
      }
      setSavingRestaurantBranding(true);
      await axios.put('/api/restaurant/branding', restaurantBranding, {
        headers: { 'x-auth-token': token }
      });
      setSuccess('Настройки брендинга ресторана сохранены');
      // Модальное окно брендинга уже закрывается через setBrandingVisible в кнопке сохранения
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Ошибка сохранения настроек');
    } finally {
      setSavingRestaurantBranding(false);
    }
  };

  // Upload logo for branding
  const handleUploadLogo = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setLogoError('');
    setLogoUploading(true);
    try {
      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('managerToken');
      if (!token) {
        setError('Токен не найден. Пожалуйста, войдите снова.');
        setLogoUploading(false);
        return;
      }
      if (!canEdit) {
        setLogoError('Недостаточно прав для загрузки логотипа');
        setLogoUploading(false);
        return;
      }
      const formData = new FormData();
      formData.append('image', file);
      const res = await axios.post('/api/upload/image', formData, {
        headers: { 'x-auth-token': token, 'Content-Type': 'multipart/form-data' }
      });
      setBrandingForm((prev) => ({ ...prev, logoUrl: res.data.url }));
      setSuccess('Логотип загружен');
    } catch (err) {
      setLogoError(err.response?.data?.message || err.message || 'Ошибка загрузки логотипа');
    } finally {
      setLogoUploading(false);
    }
  };

  // Save branding to backend
  const handleSaveBranding = async () => {
    try {
      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('managerToken');
      if (!token) {
        setError('Токен не найден. Пожалуйста, войдите снова.');
        return;
      }
      if (!canEdit) {
        setError('Недостаточно прав для управления меню');
        return;
      }
      if (menus.length > 0 && !activeMenuId) {
        setError('Выберите активное меню в разделе «Наборы меню».');
        return;
      }
      const payload = {
        branding: {
          logoUrl: brandingForm.logoUrl || '',
          footerText: brandingForm.footerText || '',
          adText: brandingForm.adText || '',
          contact: brandingForm.contact || {
            phone: '',
            email: '',
            address: '',
            website: '',
          },
          social: brandingForm.social || {
            instagram: '',
            facebook: '',
            telegram: '',
            vk: '',
            whatsapp: '',
          },
          workingHours: {
            ...brandingForm.workingHours
          },
          theme: {
            primary: brandingForm.theme?.primary || '#007bff',
            secondary: brandingForm.theme?.secondary || '#6c757d',
            background: brandingForm.theme?.background || '#ffffff',
            surface: brandingForm.theme?.surface || '#ffffff',
            text: brandingForm.theme?.text || '#212529',
            border: brandingForm.theme?.border || '#dee2e6',
            success: brandingForm.theme?.success || '#28a745',
            error: brandingForm.theme?.error || '#dc3545',
            opacity: brandingForm.theme?.opacity || 0.9
          },
          backgroundImage: brandingForm.backgroundImage || {
            url: '',
            opacity: 0.1,
            position: 'center',
            size: 'cover',
            repeat: 'no-repeat'
          },
          slideshow: brandingForm.slideshow || {
            enabled: false,
            images: [],
            autoplay: true,
            interval: 3000,
            showIndicators: true,
            showArrows: true
          }
        }
      };
      
      console.log('Saving branding with slideshow data:', payload.branding.slideshow);
      
      
      const res = await axios.put(
        `/api/menu/${restaurant._id}/menus/${activeMenuId}/branding`,
        payload,
        { headers: { 'x-auth-token': token } }
      );
      
      console.log('Branding save response:', res.data.branding.slideshow);
      setMenu(res.data);
      setSuccess('Конфигурация меню сохранена');
      setError('');
      setBrandingVisible(false); // Закрыть модальное окно после успешного сохранения
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Не удалось сохранить брендинг');
      setSuccess('');
    }
  };

  // Функция для сброса цветов к значениям по умолчанию
  const resetColorsToDefault = () => {
    setBrandingForm({
      ...brandingForm,
      theme: {
        primary: '#007bff',
        secondary: '#6c757d',
        background: '#ffffff',
        surface: '#ffffff',
        text: '#212529',
        border: '#dee2e6',
        success: '#2c67b5',
        error: '#dc3545',
        opacity: 0.9
      }
    });
  };
  
  const handleAddItem = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('managerToken');
      
      // Check if token exists
      if (!token) {
        setError('Токен не найден. Пожалуйста, войдите снова.');
        return;
      }
      if (!canEdit) {
        setError('Недостаточно прав для управления меню');
        return;
      }
      if (menus.length > 0 && !activeMenuId) {
        setError('Выберите активное меню в разделе «Наборы меню».');
        return;
      }
      
      // Проверка обязательных полей
      if (!newItem.name || !newItem.name.trim()) {
        setError('Название блюда обязательно для заполнения');
        return;
      }
      
      if (!newItem.category || !newItem.category.trim()) {
        setError('Выберите категорию для блюда');
        return;
      }
      
      if (!newItem.price || isNaN(Number(newItem.price)) || Number(newItem.price) <= 0) {
        setError('Укажите корректную цену блюда');
        return;
      }
      
      const base = activeMenuId
        ? `/api/menu/${restaurant._id}/menus/${activeMenuId}`
        : `/api/menu/${restaurant._id}`;
      const payload = {
        ...newItem,
        addons: Array.isArray(newItem.addons)
          ? newItem.addons
              .map(a => ({ name: String(a.name || '').trim(), price: Number(a.price) || 0 }))
              .filter(a => a.name.length > 0 && a.price >= 0)
          : [],
      };
      
      // Очищаем ошибки перед отправкой
      setError('');
      
      const res = await axios.post(
        `${base}/items${activeMenuId ? '' : `?mode=${menuMode}`}`,
        payload,
        { headers: { 'x-auth-token': token } }
      );
      
      setMenu(res.data);
      setSuccess('Пункт меню успешно добавлен');
      setError('');
      
      // Reset form
      setNewItem({
        name: '',
        description: '',
        price: '',
        category: '',
        image: '',
        status: 'active',
        isAvailable: true,
        addons: [],
        addonGroups: [],
        groupOptionIds: [],
      });

      // sync status map from updated menu
      const map = {};
      (res.data.items || []).forEach(it => {
        map[it._id] = it.status || (it.isAvailable ? 'active' : 'hidden');
      });
      setItemStatusMap(map);
    } catch (err) {
      console.error('Error adding menu item:', err);
      if (err.response) {
        // Server responded with error status
        setError(`Ошибка сервера: ${err.response.data.message || err.response.statusText}`);
      } else if (err.request) {
        // Request was made but no response received
        setError('Ошибка сети: Не удалось получить ответ от сервера');
      } else {
        // Something else happened
        setError(`Ошибка: ${err.message}`);
      }
      setSuccess('');
    }
  };
  
  const handleUpdateItem = async (itemId, update = {}) => {
    try {
      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('managerToken');
      if (!token) {
        setError('Токен не найден. Пожалуйста, войдите снова.');
        return;
      }
      if (!canEdit) {
        setError('Недостаточно прав для управления меню');
        return;
      }
      const payload = { ...update };
      const base = activeMenuId
        ? `/api/menu/${restaurant._id}/menus/${activeMenuId}`
        : `/api/menu/${restaurant._id}`;
      const res = await axios.put(
        `${base}/items/${itemId}${activeMenuId ? '' : `?mode=${menuMode}`}`,
        payload,
        { headers: { 'x-auth-token': token } }
      );
      setMenu(res.data);
      setSuccess('Пункт меню обновлен');
      setError('');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Не удалось обновить пункт меню';
      setError(msg);
      setSuccess('');
    }
  };

  const beginEditItem = (item) => {
    if (!item || !item._id) return;
    setEditItemMap((prev) => ({
      ...prev,
      [item._id]: {
        name: item.name || '',
        description: item.description || '',
        price: item.price || 0,
        category: item.category || '',
        image: item.image || '',
        weight: typeof item.weight === 'number' ? String(item.weight) : (item.weight || ''),
        weightUnit: item.weightUnit || 'г',
        status: item.status || (item.isAvailable ? 'active' : 'hidden'),
        addons: Array.isArray(item.addons) ? item.addons.map(a => ({ name: a.name || '', price: Number(a.price) || 0 })) : [],
        addonGroups: Array.isArray(item.addonGroups)
          ? item.addonGroups.map(g => ({
              name: g?.name || '',
              type: g?.type === 'single' ? 'single' : 'multiple',
              required: !!g?.required,
              maxSelect: g?.maxSelect,
              options: Array.isArray(g?.options)
                ? g.options.map(o => ({ name: o?.name || '', price: Number(o?.price) || 0 }))
                : []
            }))
          : [],
        groupOptionIds: Array.isArray(item.groupOptionIds) ? item.groupOptionIds : []
      }
    }));
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setEditModalVisible(true);
    beginEditItem(item);
  };


  // Функция для загрузки изображения в редактировании
  const handleEditImageUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    setUploadError('');
    setUploading(true);

    try {
      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('managerToken');
      if (!token) {
        setError('Токен не найден. Пожалуйста, войдите снова.');
        setUploading(false);
        return;
      }
      if (!canEdit) {
        setUploadError('Недостаточно прав для загрузки изображений');
        setUploading(false);
        return;
      }

      const formData = new FormData();
      formData.append('image', file);

      const res = await axios.post('/api/upload/image', formData, {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Обновляем изображение в редактируемом элементе
      onEditItemChange(editingItem._id, 'image', res.data.url);
      setSuccess('Изображение загружено');
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Ошибка загрузки файла';
      setUploadError(message);
    } finally {
      setUploading(false);
    }
  };

  const cancelEditItem = (itemId) => {
    setEditItemMap((prev) => {
      const clone = { ...prev };
      delete clone[itemId];
      return clone;
    });
  };

  const onEditItemChange = (itemId, field, value) => {
    setEditItemMap((prev) => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || {}), [field]: value }
    }));
  };

  // Addons edit helpers
  const addEditAddonRow = (itemId) => {
    setEditItemMap((prev) => {
      const current = prev[itemId] || {};
      const list = Array.isArray(current.addons) ? current.addons.slice() : [];
      list.push({ name: '', price: 0 });
      return { ...prev, [itemId]: { ...current, addons: list } };
    });
  };
  const updateEditAddonField = (itemId, idx, field, value) => {
    setEditItemMap((prev) => {
      const current = prev[itemId] || {};
      const list = Array.isArray(current.addons) ? current.addons.slice() : [];
      if (list[idx]) {
        list[idx] = { ...list[idx], [field]: field === 'price' ? Number(value) : value };
      }
      return { ...prev, [itemId]: { ...current, addons: list } };
    });
  };
  const removeEditAddonRow = (itemId, idx) => {
    setEditItemMap((prev) => {
      const current = prev[itemId] || {};
      const list = Array.isArray(current.addons) ? current.addons.slice() : [];
      list.splice(idx, 1);
      return { ...prev, [itemId]: { ...current, addons: list } };
    });
  };

  // Addon groups edit helpers
  const updateEditGroupField = (itemId, groupIdx, field, value) => {
    setEditItemMap((prev) => {
      const current = prev[itemId] || {};
      const list = Array.isArray(current.addonGroups) ? current.addonGroups.slice() : [];
      if (!list[groupIdx]) list[groupIdx] = { name: '', type: 'multiple', required: false, options: [] };
      list[groupIdx] = { ...list[groupIdx], [field]: value };
      return { ...prev, [itemId]: { ...current, addonGroups: list } };
    });
  };
  const addEditGroupRow = (itemId) => {
    setEditItemMap((prev) => {
      const current = prev[itemId] || {};
      const list = Array.isArray(current.addonGroups) ? current.addonGroups.slice() : [];
      list.push({ name: '', type: 'multiple', required: false, maxSelect: undefined, options: [] });
      return { ...prev, [itemId]: { ...current, addonGroups: list } };
    });
  };
  const removeEditGroupRow = (itemId, groupIdx) => {
    setEditItemMap((prev) => {
      const current = prev[itemId] || {};
      const list = Array.isArray(current.addonGroups) ? current.addonGroups.slice() : [];
      list.splice(groupIdx, 1);
      return { ...prev, [itemId]: { ...current, addonGroups: list } };
    });
  };

  const saveEditItem = async (itemId) => {
    const payloadRaw = editItemMap[itemId];
    if (!payloadRaw) return;
    
    // Проверка обязательных полей
    if (!payloadRaw.name || !payloadRaw.name.trim()) {
      setError('Название блюда обязательно для заполнения');
        return;
      }
    
    if (!payloadRaw.category || !payloadRaw.category.trim()) {
      setError('Выберите категорию для блюда');
        return;
      }
    
    if (!payloadRaw.price || isNaN(Number(payloadRaw.price)) || Number(payloadRaw.price) <= 0) {
      setError('Укажите корректную цену блюда');
        return;
      }
    
    // sanitize addons
    const addons = Array.isArray(payloadRaw.addons)
      ? payloadRaw.addons
          .map(a => ({ name: String(a.name || '').trim(), price: Number(a.price) || 0 }))
          .filter(a => a.name.length > 0 && a.price >= 0)
      : undefined;
    // sanitize addonGroups
    const addonGroups = Array.isArray(payloadRaw.addonGroups)
      ? payloadRaw.addonGroups
          .map(g => ({
            name: String((g?.name) || '').trim(),
            type: (g?.type === 'single' ? 'single' : 'multiple'),
            required: !!g?.required,
            maxSelect: g?.maxSelect ? Number(g.maxSelect) : undefined,
            options: Array.isArray(g?.options)
              ? g.options
                  .map(o => ({ name: String((o?.name) || '').trim(), price: Number(o?.price) || 0 }))
                  .filter(o => o.name.length > 0 && o.price >= 0)
              : []
          }))
          .filter(g => g.name.length > 0)
      : undefined;
    const payload = { ...payloadRaw };
    if (addons !== undefined) payload.addons = addons;
    if (addonGroups !== undefined) payload.addonGroups = addonGroups;
    
    // Очищаем ошибки перед сохранением
      setError('');
    
    await handleUpdateItem(itemId, payload);
    cancelEditItem(itemId);
  };

  const handleDeleteItem = async (itemId) => {
    try {
      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('managerToken');
      
      // Check if token exists
      if (!token) {
        setError('Токен не найден. Пожалуйста, войдите снова.');
        return;
      }
      
      const base = activeMenuId
        ? `/api/menu/${restaurant._id}/menus/${activeMenuId}`
        : `/api/menu/${restaurant._id}`;
      const res = await axios.delete(
        `${base}/items/${itemId}${activeMenuId ? '' : `?mode=${menuMode}`}`,
        { headers: { 'x-auth-token': token } }
      );
      
      setMenu(res.data);
      setSuccess('Пункт меню успешно удален');
      setError('');
    } catch (err) {
      console.error('Error deleting menu item:', err);
      if (err.response) {
        // Server responded with error status
        setError(`Ошибка сервера: ${err.response.data.message || err.response.statusText}`);
      } else if (err.request) {
        // Request was made but no response received
        setError('Ошибка сети: Не удалось получить ответ от сервера');
      } else {
        // Something else happened
        setError(`Ошибка: ${err.message}`);
      }
      setSuccess('');
    }
  };

  // Функция для быстрого переключения статуса блюда
  const handleToggleItemStatus = async (item) => {
    try {
      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('managerToken');
      
      if (!token) {
        setError('Токен не найден. Пожалуйста, войдите снова.');
        return;
      }

      // Определяем следующий статус
      const statusCycle = {
        'active': 'hidden',
        'hidden': 'coming_soon',
        'coming_soon': 'active'
      };
      const currentStatus = item.status || 'active';
      const newStatus = statusCycle[currentStatus] || 'active';

      const base = activeMenuId
        ? `/api/menu/${restaurant._id}/menus/${activeMenuId}`
        : `/api/menu/${restaurant._id}`;
      
      const res = await axios.put(
        `${base}/items/${item._id}${activeMenuId ? '' : `?mode=${menuMode}`}`,
        { status: newStatus },
        { headers: { 'x-auth-token': token } }
      );
      
      setMenu(res.data);
      const statusText = newStatus === 'active' ? 'Активно' : newStatus === 'hidden' ? 'Скрыто' : 'Скоро';
      setSuccess(`Статус блюда изменен на "${statusText}"`);
      setError('');
    } catch (err) {
      console.error('Error toggling item status:', err);
      if (err.response) {
        setError(`Ошибка сервера: ${err.response.data.message || err.response.statusText}`);
      } else if (err.request) {
        setError('Ошибка сети: Не удалось получить ответ от сервера');
      } else {
        setError(`Ошибка: ${err.message}`);
      }
      setSuccess('');
    }
  };
  
  if (loading) {
    return <div className="mt-5 text-center">Загрузка...</div>;
  }
  
  if (error) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <Alert variant="danger">
              <Alert.Heading>Ошибка загрузки данных</Alert.Heading>
              <p>{error}</p>
              <Button 
                variant="primary" 
                onClick={() => {
                  setError('');
                  setSuccess('');
                }}
              >
                Попробовать снова
              </Button>
            </Alert>
          </Col>
        </Row>
      </Container>
    );
  }
  
  return (
    <Container className="mt-5">
      <style>{categoryScrollStyles}</style>
      <Row className="mb-4">
        <Col>
          <h1>Управление меню</h1>
          <p>Управляйте пунктами меню и категориями вашего ресторана.</p>
        </Col>
      </Row>
      
      {success && <Alert variant="success">{success}</Alert>}
      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between">
            <Card.Title className="mb-0">Наборы меню</Card.Title>
            <div className="d-flex align-items-center gap-2">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => setBrandingVisible(true)}
              >
                Конфигуратор
              </Button>
            </div>
          </div>
          <p className="text-muted mb-2">Создавайте, переименовывайте и выбирайте активное меню для редактирования.</p>

          {menus.length === 0 ? (
            <div className="mb-3">Меню еще не созданы. Создайте первое меню ниже.</div>
          ) : (
            menus.map((m) => (
              <div key={m._id} className={`border rounded p-2 mb-2 ${activeMenuId === m._id ? 'bg-light' : ''}`}>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Form.Check
                    type="radio"
                    name="activeMenu"
                    id={`menu-active-${m._id}`}
                    checked={activeMenuId === m._id}
                    onChange={() => handleSelectMenu(m._id)}
                    label="Активное"
                  />
                  <Form.Control
                    size="sm"
                    style={{ maxWidth: 240 }}
                    value={(renameMap[m._id]?.title) ?? m.title}
                    onChange={(e) => setRenameField(m._id, 'title', e.target.value)}
                    placeholder="Название"
                    disabled={!canEdit}
                  />
                  <Form.Select
                    size="sm"
                    style={{ maxWidth: 180 }}
                    value={(renameMap[m._id]?.mode) ?? m.mode}
                    onChange={(e) => setRenameField(m._id, 'mode', e.target.value)}
                    disabled={!canEdit}
                  >
                    <option value="dine_in">В зале</option>
                    <option value="delivery">Доставка</option>
                  </Form.Select>
                  <Form.Control
                    size="sm"
                    style={{ maxWidth: 260 }}
                    type="text"
                    value={(renameMap[m._id]?.expressPayToken) ?? (m.expressPayToken || '')}
                    onChange={(e) => setRenameField(m._id, 'expressPayToken', e.target.value)}
                    placeholder="ExpressPay Token (опционально)"
                    disabled={!canEdit}
                  />
                  <Form.Control
                    size="sm"
                    style={{ maxWidth: 220 }}
                    type="password"
                    value={(renameMap[m._id]?.expressPaySecret) ?? (m.expressPaySecret || '')}
                    onChange={(e) => setRenameField(m._id, 'expressPaySecret', e.target.value)}
                    placeholder="ExpressPay Secret (опционально)"
                    disabled={!canEdit}
                  />
                  {(((renameMap[m._id]?.mode) ?? m.mode) === 'delivery') && (
                    <div className="d-flex align-items-center gap-2">
                      <Form.Check
                        type="switch"
                        id={`pay-cash-${m._id}`}
                        label="Наличными"
                        checked={((renameMap[m._id]?.paymentOptions?.cash) ?? (m.paymentOptions?.cash ?? true)) === true}
                        onChange={(e) => setRenameField(m._id, 'paymentOptions', {
                          ...(renameMap[m._id]?.paymentOptions ?? m.paymentOptions ?? { cash: true, expresspay: true }),
                          cash: e.target.checked
                        })}
                        disabled={!canEdit}
                      />
                      <Form.Check
                        type="switch"
                        id={`pay-qr-${m._id}`}
                        label="QR (ЕРИП)"
                        checked={((renameMap[m._id]?.paymentOptions?.expresspay) ?? (m.paymentOptions?.expresspay ?? true)) === true}
                        onChange={(e) => setRenameField(m._id, 'paymentOptions', {
                          ...(renameMap[m._id]?.paymentOptions ?? m.paymentOptions ?? { cash: true, expresspay: true }),
                          expresspay: e.target.checked
                        })}
                        disabled={!canEdit}
                      />
                    </div>
                  )}
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => handleRenameMenu(m._id)}
                  >
                    Сохранить
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-success"
                    onClick={() => openQR(m._id)}
                    disabled={((renameMap[m._id]?.mode) ?? m.mode) === 'dine_in'}
                  >
                    QR-код
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => handleDeleteMenu(m._id)}
                    disabled={!canEdit}
                  >
                    Удалить
                  </Button>
                  <div className="text-muted ms-auto small">
                    Пунктов: {m.itemsCount} | Категорий: {m.categoriesCount} | Обновлено:{' '}
                    {new Date(m.updatedAt).toLocaleString()}
                  </div>
                </div>
              </div>
            ))
          )}

          <hr />
          <div className="d-flex align-items-end gap-2 flex-wrap">
            <div>
              <Form.Label className="mb-1">Название нового меню</Form.Label>
              <Form.Control
                size="sm"
                value={newMenuTitle}
                onChange={(e) => setNewMenuTitle(e.target.value)}
                placeholder="Например: Основное, Завтрак"
                disabled={!canEdit}
              />
            </div>
            <div>
              <Form.Label className="mb-1">Тип</Form.Label>
              <Form.Select
                size="sm"
                value={newMenuMode}
                onChange={(e) => setNewMenuMode(e.target.value)}
                disabled={!canEdit}
              >
                <option value="dine_in">В зале</option>
                <option value="delivery">Доставка</option>
              </Form.Select>
            </div>
            <div>
              <Form.Label className="mb-1">ExpressPay Token (опционально)</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={newMenuExpressPayToken}
                onChange={(e) => setNewMenuExpressPayToken(e.target.value)}
                placeholder="Токен для этого меню"
                disabled={!canEdit}
              />
            </div>
            <div>
              <Form.Label className="mb-1">ExpressPay Secret (опционально)</Form.Label>
              <Form.Control
                size="sm"
                type="password"
                value={newMenuExpressPaySecret}
                onChange={(e) => setNewMenuExpressPaySecret(e.target.value)}
                placeholder="Секретное слово для этого меню"
                disabled={!canEdit}
              />
            </div>
            <div className="pt-4">
              <Button variant="outline-primary" size="sm" onClick={handleCreateMenu} disabled={!canEdit}>
                Создать меню
              </Button>
            </div>
          </div>

          {/* Copy items section */}
          {menus.length > 1 && activeMenuId && (
            <div className="mt-4 p-3 border rounded bg-light">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">📋 Копировать блюда из другого меню</h6>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setShowCopyOptions(!showCopyOptions)}
                >
                  {showCopyOptions ? 'Скрыть' : 'Показать'}
                </Button>
              </div>
              
              {showCopyOptions && (
                <div>
                  <Row className="g-2 mb-3">
                    <Col md={6}>
                      <Form.Label className="mb-1">Копировать из меню:</Form.Label>
                      <Form.Select
                        size="sm"
                        value={copyFromMenuId}
                        onChange={(e) => setCopyFromMenuId(e.target.value)}
                        disabled={!canEdit}
                      >
                        <option value="">Выберите меню</option>
                        {menus
                          .filter(m => m._id !== activeMenuId)
                          .map((m) => (
                            <option key={m._id} value={m._id}>
                              {m.title} ({m.mode === 'delivery' ? 'Доставка' : 'В зале'}) - {m.itemsCount} блюд
                            </option>
                          ))}
                      </Form.Select>
                    </Col>
                    <Col md={6}>
                      <Form.Label className="mb-1">Опции копирования:</Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Check
                          type="checkbox"
                          id="copy-categories"
                          label="Копировать категории"
                          checked={copyCategories}
                          onChange={(e) => setCopyCategories(e.target.checked)}
                          disabled={!canEdit}
                        />
                      </div>
                    </Col>
                  </Row>

                  {copyFromMenuId && (
                    <CopyItemsSelector
                      sourceMenuId={copyFromMenuId}
                      selectedItems={selectedItemsToCopy}
                      onSelectionChange={setSelectedItemsToCopy}
                      copyAllItems={copyAllItems}
                      onCopyAllChange={setCopyAllItems}
                      canEdit={canEdit}
                      getMenuItems={getMenuItems}
                    />
                  )}

                  <div className="mt-3 d-flex gap-2">
                    <Button
                      size="sm"
                      variant="success"
                      onClick={handleCopyItems}
                      disabled={!canEdit || !copyFromMenuId || (!copyAllItems && selectedItemsToCopy.length === 0)}
                    >
                      📋 Копировать выбранное
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => {
                        setCopyFromMenuId('');
                        setShowCopyOptions(false);
                        setCopyAllItems(false);
                        setSelectedItemsToCopy([]);
                        setCopyCategories(true);
                      }}
                    >
                      Отмена
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
       
      {/* Tabs for different management sections */}
      <Tabs defaultActiveKey="menu" id="menu-management-tabs" className="mb-4">
        <Tab eventKey="menu" title="Меню">
      <Row className="g-0 align-items-stretch" style={{ minHeight: 'calc(100vh - 200px)' }}>
        <Col md={6} className="pe-2 d-flex">
          <Card className="mb-4 shadow-sm w-100">
            <Card.Header className="bg-primary text-white">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <span className="me-2"></span>
                  <h5 className="mb-0">Добавить новый пункт меню</h5>
                </div>
                  {menus.length > 0 && (
                  <Badge bg="light" text="dark" className="px-2 py-1">
                    {(menus.find(m => m._id === activeMenuId)?.title) || 'не выбрано'} — {(menus.find(m => m._id === activeMenuId)?.mode === 'delivery' ? 'Доставка' : 'В зале')}
                  </Badge>
                  )}
                </div>
            </Card.Header>
            <Card.Body className="p-4" style={{ padding: '20px' }}>
              {!canEdit && (
                <Alert variant="warning" className="mb-3">
                  <div className="d-flex align-items-center">
                    <span className="me-2"></span>
                  У вас нет прав на управление меню. Обратитесь к владельцу ресторана.
                  </div>
                </Alert>
              )}
              {menus.length > 0 && !activeMenuId && (
                <Alert variant="info" className="mb-3">
                  <div className="d-flex align-items-center">
                    <span className="me-2"></span>
                  Выберите активное меню в разделе «Наборы меню», чтобы добавлять блюда и категории именно в него.
                  </div>
                </Alert>
              )}
              <Form onSubmit={handleAddItem}>
                {/* Основная информация */}
                <Row className="g-2 mb-3">
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label className="fw-semibold"> Название блюда *</Form.Label>
                  <Form.Control
                    type="text"
                    value={newItem.name}
                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                        disabled={!canEdit}
                        placeholder="Например: Маргарита"
                    required
                  />
                </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">📂 Категория</Form.Label>
                  {menu && menu.categories && menu.categories.length > 0 ? (
                    <Form.Select
                      value={newItem.category}
                      onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                          disabled={!canEdit}
                    >
                      <option value="">Выберите категорию</option>
                      {menu.categories.map((c, idx) => (
                        <option key={idx} value={c.name}>{c.name}</option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control
                      type="text"
                      value={newItem.category}
                      onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                          disabled={!canEdit}
                      placeholder="Введите категорию"
                    />
                  )}
                </Form.Group>
                  </Col>
                </Row>

                {/* Описание и цена */}
                <Row className="g-2 mb-3">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold"> Описание</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={newItem.description}
                        onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                        disabled={!canEdit}
                        placeholder="Опишите блюдо..."
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">💵 Цена (BYN) *</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={newItem.price}
                        onChange={(e) => setNewItem({ ...newItem, price: parseFloat(e.target.value) || 0 })}
                        disabled={!canEdit}
                        placeholder="0.00"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-semibold"> Вес</Form.Label>
                      <Form.Control
                        type="text"
                        value={newItem.weight || ''}
                        onChange={(e) => setNewItem({ ...newItem, weight: e.target.value })}
                        disabled={!canEdit}
                        placeholder="250 или 120/10/5"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                  <Form.Label>Статус блюда</Form.Label>
                  <Form.Select className="mb-3"
                    value={newItem.status}
                    onChange={(e) => setNewItem({ ...newItem, status: e.target.value })}
                  >
                    <option value="active">Активно </option>
                    <option value="coming_soon">Скоро</option>
                    <option value="hidden">Скрыто</option>
                  </Form.Select>
                </Form.Group>
                  </Col>
                </Row>
                

                <Form.Group className="mb-3">
                  <Form.Label>Загрузить изображение</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handleUploadImage}
                    disabled={uploading}
                  />
                  <Form.Text className="text-muted">
                    Поддерживаются форматы: JPEG, PNG, GIF, WebP, HEIC (до 50MB)
                  </Form.Text>
                  {uploading && <div className="text-muted mt-1">Загрузка...</div>}
                  {uploadError && <div className="text-danger mt-1">{uploadError}</div>}
                  {newItem.image && (
                    <div className="mt-2">
                      <img
                        src={newItem.image}
                        alt="Предпросмотр"
                        style={{ maxWidth: '160px', maxHeight: '120px', objectFit: 'cover' }}
                        className="rounded border"
                      />
                    </div>
                  )}
                </Form.Group>
                
             {/* Дополнения */}
             <div className="mb-3 p-3 border rounded bg-light">
                  <div 
                    className="d-flex justify-content-between align-items-center mb-3"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setAddonsExpanded(!addonsExpanded)}
                  >
                    <Form.Label className="fw-bold mb-0">➕ Дополнения</Form.Label>
                    <span className={`fs-4 transition-all ${addonsExpanded ? 'rotate-180' : ''}`}>
                      {addonsExpanded ? '▼' : '▶'}
                    </span>
                  </div>
                  
                  {addonsExpanded && (
                    <Tabs defaultActiveKey="simple-addons" className="mb-3" id="addons-tabs">
                      {/* Простые дополнения */}
                      <Tab eventKey="simple-addons" title="Простые дополнения">
                        <div className="p-3">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">Добавить простые дополнения</h6>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => setNewItem({ ...newItem, addons: [...(newItem.addons || []), { name: '', price: 0 }] })}
                            >
                              ➕ Добавить
                </Button>
                          </div>
        
                          {(newItem.addons || []).map((a, idx) => (
                            <div key={idx} className="card border-0 bg-white p-3 mb-2">
                              <Row className="g-2 align-items-center">
        <Col md={6}>
                  <Form.Control
                                    placeholder="Название (например, сыр)"
                                    value={a.name}
                                    onChange={(e) => {
                                      const list = (newItem.addons || []).slice();
                                      list[idx] = { ...list[idx], name: e.target.value };
                                      setNewItem({ ...newItem, addons: list });
                                    }}
                        />
                      </Col>
                                <Col md={4}>
                        <Form.Control
                                    type="number"
                                    step="0.01"
                                    placeholder="Цена (BYN)"
                                    value={a.price}
                                    onChange={(e) => {
                                      const list = (newItem.addons || []).slice();
                                      list[idx] = { ...list[idx], price: parseFloat(e.target.value) || 0 };
                                      setNewItem({ ...newItem, addons: list });
                                    }}
                        />
                      </Col>
                                <Col md={2} className="text-end">
                          <Button
                            size="sm"
                            variant="outline-danger"
                                    onClick={() => {
                                      const list = (newItem.addons || []).slice();
                                      list.splice(idx, 1);
                                      setNewItem({ ...newItem, addons: list });
                                    }}
                          >
                            Удалить
                          </Button>
                      </Col>
                    </Row>
                  </div>
                ))}
                          
                          {(!newItem.addons || newItem.addons.length === 0) && (
                            <div className="text-center text-muted py-4">
                              <span className="fs-4"></span>
                              <p className="mb-0">Пока нет дополнений</p>
                              <small>Нажмите "Добавить" чтобы создать первое дополнение</small>
                            </div>
                          )}
                        </div>
                      </Tab>
                      
                      {/* Группы дополнений */}
                      <Tab eventKey="addon-groups" title="Группы дополнений">
                        <div className="p-3">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">Группы дополнений (радио/чекбокс)</h6>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => setNewItem({ 
                                ...newItem, 
                                addonGroups: [...(newItem.addonGroups || []), { 
                                  name: '', 
                                  type: 'multiple', 
                                  required: false, 
                                  maxSelect: undefined, 
                                  options: [] 
                                }] 
                              })}
                            >
                              ➕ Добавить группу
                            </Button>
                          </div>
                          
                          {(newItem.addonGroups || []).map((g, gidx) => (
                            <div key={gidx} className="card border-0 bg-white p-3 mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">Группа {gidx + 1}</h6>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => {
                                    const list = (newItem.addonGroups || []).slice();
                                    list.splice(gidx, 1);
                                    setNewItem({ ...newItem, addonGroups: list });
                                  }}
                                >
                                  Удалить Удалить группу
                                </Button>
                              </div>
                              
                              <Row className="g-2 mb-3">
                              <Col md={6}>
                                <Form.Control
                                    placeholder="Название группы (напр. Соусы)"
                                    value={g.name}
                                    onChange={(e) => {
                                      const list = (newItem.addonGroups || []).slice();
                                      list[gidx] = { ...list[gidx], name: e.target.value };
                                      setNewItem({ ...newItem, addonGroups: list });
                                    }}
                                />
                              </Col>
                                <Col md={3}>
                                  <Form.Select
                                    value={g.type || 'multiple'}
                                    onChange={(e) => {
                                      const list = (newItem.addonGroups || []).slice();
                                      list[gidx] = { ...list[gidx], type: e.target.value };
                                      setNewItem({ ...newItem, addonGroups: list });
                                    }}
                                  >
                                    <option value="single">Один вариант</option>
                                    <option value="multiple">Несколько вариантов</option>
                                  </Form.Select>
                                </Col>
                                <Col md={3}>
                                  <Form.Control
                                    type="number"
                                    min="1"
                                    placeholder="Макс. выбор"
                                    value={g.maxSelect || ''}
                                    onChange={(e) => {
                                      const list = (newItem.addonGroups || []).slice();
                                      const val = e.target.value ? parseInt(e.target.value, 10) : undefined;
                                      list[gidx] = { ...list[gidx], maxSelect: val };
                                      setNewItem({ ...newItem, addonGroups: list });
                                    }}
                                  />
                              </Col>
                            </Row>
                              
                              <div className="d-flex align-items-center gap-3 mb-3">
                                <Form.Check
                                  type="switch"
                                  id={`greq-${gidx}`}
                                  label="Обязательная группа"
                                  checked={!!g.required}
                                  onChange={(e) => {
                                    const list = (newItem.addonGroups || []).slice();
                                    list[gidx] = { ...list[gidx], required: e.target.checked };
                                    setNewItem({ ...newItem, addonGroups: list });
                                  }}
                                />
                              </div>
                              
                              <div>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <h6 className="mb-0">Опции в группе</h6>
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => {
                                      const list = (newItem.addonGroups || []).slice();
                                      const opts = (list[gidx].options || []).slice();
                                      opts.push({ name: '', price: 0 });
                                      list[gidx] = { ...list[gidx], options: opts };
                                      setNewItem({ ...newItem, addonGroups: list });
                                    }}
                                  >
                                    ➕ Добавить опцию
                                  </Button>
                                </div>
                                
                                {(g.options || []).map((opt, oidx) => (
                                  <div key={oidx} className="card border-0 bg-light p-2 mb-2">
                                    <Row className="g-2 align-items-center">
                                      <Col md={6}>
                                <Form.Control
                                          size="sm"
                                          placeholder="Название опции"
                                          value={opt.name}
                                          onChange={(e) => {
                                            const list = (newItem.addonGroups || []).slice();
                                            const opts = (list[gidx].options || []).slice();
                                            opts[oidx] = { ...opts[oidx], name: e.target.value };
                                            list[gidx] = { ...list[gidx], options: opts };
                                            setNewItem({ ...newItem, addonGroups: list });
                                          }}
                                />
                              </Col>
                              <Col md={4}>
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  step="0.01"
                                          placeholder="Цена (BYN)"
                                          value={opt.price}
                                          onChange={(e) => {
                                            const list = (newItem.addonGroups || []).slice();
                                            const opts = (list[gidx].options || []).slice();
                                            opts[oidx] = { ...opts[oidx], price: parseFloat(e.target.value) || 0 };
                                            list[gidx] = { ...list[gidx], options: opts };
                                            setNewItem({ ...newItem, addonGroups: list });
                                          }}
                                />
                              </Col>
                                      <Col md={2} className="text-end">
                                        <Button
                                  size="sm"
                                          variant="outline-danger"
                                          onClick={() => {
                                            const list = (newItem.addonGroups || []).slice();
                                            const opts = (list[gidx].options || []).slice();
                                            opts.splice(oidx, 1);
                                            list[gidx] = { ...list[gidx], options: opts };
                                            setNewItem({ ...newItem, addonGroups: list });
                                          }}
                                        >
                                          Удалить
                                        </Button>
                              </Col>
                            </Row>
                          </div>
                                ))}
                                
                                {(!g.options || g.options.length === 0) && (
                                  <div className="text-center text-muted py-3">
                                    <span className="fs-5">📋</span>
                                    <p className="mb-0">Нет опций в группе</p>
                                    <small>Добавьте опции для этой группы</small>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          
                          {(!newItem.addonGroups || newItem.addonGroups.length === 0) && (
                            <div className="text-center text-muted py-4">
                              <span className="fs-4">📦</span>
                              <p className="mb-0">Пока нет групп дополнений</p>
                              <small>Нажмите "Добавить группу" чтобы создать первую группу</small>
                            </div>
                          )}
                        </div>
                      </Tab>
                    </Tabs>
                  )}
                </div>
  
                
                {/* Group Options Selection */}
                {groupOptions.length > 0 && (
                  <Form.Group className="mb-3">
                    <div className="mb-3 p-3 border rounded bg-light">
                      <div 
                        className="d-flex justify-content-between align-items-center mb-3"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setGroupOptionsExpanded(!groupOptionsExpanded)}
                      >
                        <Form.Label className="fw-bold mb-0">🔧 Группы опций</Form.Label>
                        <span className={`fs-4 transition-all ${groupOptionsExpanded ? 'rotate-180' : ''}`}>
                          {groupOptionsExpanded ? '▼' : '▶'}
                        </span>
                      </div>
                      
                      {groupOptionsExpanded && (
                        <>
                          {groupOptions.length > 0 ? (
                            <Tabs defaultActiveKey={groupOptions[0]?._id} className="mb-3" id="group-options-tabs">
                              {groupOptions.map((group) => (
                            <Tab 
                              key={group._id} 
                              eventKey={group._id} 
                              title={
                                <div className="d-flex align-items-center gap-2">
                                  <span>{group.name}</span>
                                  {newItem.groupOptionIds?.includes(group._id) && (
                                    <span className="badge bg-success">✓</span>
                                  )}
                                </div>
                              }
                            >
                              <div className="p-3">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <div>
                                    <h6 className="mb-1">{group.name}</h6>
                                    {group.description && (
                                      <small className="text-muted">{group.description}</small>
                                    )}
                                  </div>
                                  <Form.Check
                                    type="switch"
                                    id={`group-switch-${group._id}`}
                                    label="Включить группу"
                                    checked={newItem.groupOptionIds?.includes(group._id) || false}
                                    onChange={(e) => {
                                      const currentIds = newItem.groupOptionIds || [];
                                      if (e.target.checked) {
                                        setNewItem({
                                          ...newItem,
                                          groupOptionIds: [...currentIds, group._id]
                                        });
                                      } else {
                                        setNewItem({
                                          ...newItem,
                                          groupOptionIds: currentIds.filter(id => id !== group._id)
                                        });
                                      }
                                    }}
                                  />
                                </div>
                                
                                {group.options && group.options.length > 0 && (
                                  <div>
                                    <h6 className="mb-2">Опции в группе:</h6>
                                    <div className="row g-2">
                                      {group.options.map((option, idx) => (
                                        <div key={idx} className="col-md-6">
                                          <div className="card border-0 bg-white p-2">
                                            <div className="d-flex justify-content-between align-items-center">
                                              <span className="fw-semibold">{option.name}</span>
                                              <span className="badge bg-primary">
                                                {option.price > 0 ? `+${option.price} BYN` : 'Бесплатно'}
                                              </span>
                                            </div>
                                            {option.isRequired && (
                                              <small className="text-warning">Обязательно</small>
                                            )}
                                            {option.isMultiple && (
                                              <small className="text-info">Можно выбрать несколько</small>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </Tab>
                              ))}
                            </Tabs>
                          ) : (
                            <div className="text-center p-4 text-muted">
                              <p className="mb-2">Группы опций не созданы</p>
                              <small>Перейдите в раздел "Группы опций" для создания групп опций</small>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </Form.Group>
                )}
                
              <Row className="g-2 align-items-center">
                <Col md={12} className="text-md-end">
                              <Button
                    variant="success" 
                    type="submit" 
                    disabled={!canEdit || (menus.length > 0 && !activeMenuId)}
                    className="px-3 py-2"
                  >
                    <span className="me-1">➕</span>
                    Добавить блюдо
                              </Button>
                </Col>
              </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6} className="ps-2 d-flex">
          {/* Menu Items by Categories */}
          {menu && menu.items && menu.items.length > 0 && (
            <Card className="mb-4 w-100">
              <Card.Header className="bg-primary text-white">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <span className="me-2">📋</span>
                    <h5 className="mb-0">Пункты меню по категориям</h5>
                  </div>
                  <Badge bg="light" text="dark" className="px-2 py-1">
                    {Object.values(itemsByCategory || {}).reduce((total, items) => total + items.length, 0)} блюд
                    {dishSearchTerm && ` (найдено)`}
                  </Badge>
                </div>
              </Card.Header>
              <Card.Body className="d-flex flex-column" style={{ padding: '20px' }}>
                {/* Search Controls */}
                <div className="d-flex gap-2 align-items-center mb-3">
                  <Form.Control
                    size="sm"
                    type="text"
                    placeholder="Поиск по блюдам..."
                    value={dishSearchTerm}
                    onChange={(e) => setDishSearchTerm(e.target.value)}
                    style={{ width: '200px' }}
                  />
                  {Object.keys(itemsByCategory || {}).length > 1 && (
                    <Form.Control
                      size="sm"
                      type="text"
                      placeholder="Поиск категории..."
                      value={categorySearchTerm}
                      onChange={(e) => setCategorySearchTerm(e.target.value)}
                      style={{ width: '150px' }}
                    />
                  )}
                  {(dishSearchTerm || categorySearchTerm) && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setDishSearchTerm('');
                        setCategorySearchTerm('');
                      }}
                      title="Очистить поиск"
                    >
                      <i className="fas fa-times"></i>
                    </Button>
                  )}
                </div>
                
                {/* Category Tabs */}
                {Object.keys(itemsByCategory || {}).length > 1 && (
                  <div className="mb-3">
                    <div 
                      className="d-flex overflow-auto pb-2 gap-2 category-scroll-container"
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseLeave}
                    >
                      <Button
                        size="sm"
                        variant={categoryFilter === 'all' ? 'primary' : 'outline-secondary'}
                        onClick={(e) => handleButtonClick(e, () => setCategoryFilter('all'))}
                        className="d-flex align-items-center gap-1 flex-shrink-0"
                        style={{ borderRadius: '20px', padding: '4px 12px' }}
                      >
                        <span>Все</span>
                        <Badge bg={categoryFilter === 'all' ? 'light' : 'secondary'} text={categoryFilter === 'all' ? 'dark' : 'light'}>
                          {menu.items.length}
                        </Badge>
                      </Button>
                      {Object.keys(itemsByCategory || {}).map(categoryName => (
                        <Button
                          key={categoryName}
                          size="sm"
                          variant={categoryFilter === categoryName ? 'primary' : 'outline-secondary'}
                          onClick={(e) => handleButtonClick(e, () => setCategoryFilter(categoryName))}
                          className="d-flex align-items-center gap-1 flex-shrink-0"
                          style={{ borderRadius: '20px', padding: '4px 12px' }}
                        >
                          <span>{categoryName}</span>
                          <Badge bg={categoryFilter === categoryName ? 'light' : 'secondary'} text={categoryFilter === categoryName ? 'dark' : 'light'}>
                            {(itemsByCategory[categoryName] || []).length}
                          </Badge>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Menu Items with Vertical Scroll - Flex grow to fill remaining space */}
                <div 
                  className="menu-items-scroll-container flex-grow-1"
                  style={{
                    maxHeight: 'calc(100vh - 300px)',
                    overflowY: 'auto',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    padding: '15px',
                    minHeight: '0'
                  }}
                >
                  {(() => {
                    // Фильтруем категории в зависимости от выбранного фильтра
                    let filteredCategories = Object.entries(itemsByCategory || {}).filter(([categoryName]) => {
                      if (categoryFilter === 'all') return true;
                      if (categoryFilter === 'no-category') return categoryName === 'Без категории';
                      return categoryName === categoryFilter;
                    });

                    // Дополнительная фильтрация по поиску категорий
                    if (categorySearchTerm) {
                      filteredCategories = filteredCategories.filter(([categoryName]) => 
                        categoryName.toLowerCase().includes(categorySearchTerm.toLowerCase())
                      );
                    }

                    if (filteredCategories.length === 0) {
                      return (
                        <div className="text-center text-muted py-4">
                          <div className="mb-2">🍽️</div>
                          <p className="mb-0">
                            {categoryFilter === 'all' 
                              ? 'Нет блюд в меню' 
                              : `Нет блюд в категории "${categoryFilter === 'no-category' ? 'Без категории' : categoryFilter}"`
                            }
                          </p>
                        </div>
                      );
                    }

                    return filteredCategories.map(([categoryName, items]) => (
                      <div key={categoryName} className="mb-4">
                        <div className="d-flex align-items-center mb-2">
                          <span className="me-2">📋</span>
                          <h6 className="mb-0">{categoryName}</h6>
                          <Badge bg="secondary" className="ms-2">{items.length}</Badge>
                        </div>
                        <div className="ps-4">
                          {items.map((item) => (
                            <div key={item._id} className="border rounded p-2 mb-2 bg-light">
                              <div className="d-flex justify-content-between align-items-start">
                                <div className="flex-grow-1">
                                  <div className="fw-semibold">{item.name}</div>
                                  {item.description && (
                                    <small className="text-muted d-block">{item.description}</small>
                                  )}
                                  <div className="mt-1">
                                    <Badge bg="success" className="me-2">{item.price.toFixed(2)} BYN</Badge>
                                    {item.weight && (
                                      <Badge bg="info" className="me-2">{item.weight} {item.weightUnit || 'г'}</Badge>
                                    )}
                                    <Badge 
                                      bg={item.status === 'active' ? 'success' : item.status === 'coming_soon' ? 'warning' : 'secondary'}
                                      className="me-2"
                                    >
                                      {item.status === 'active' ? 'Активно' : 
                                       item.status === 'coming_soon' ? 'Скоро' : 'Скрыто'}
                                    </Badge>
                                  </div>
                                </div>
                                <div className="d-flex gap-1">
                                <Button
                                  variant={item.status === 'active' ? 'success' : item.status === 'coming_soon' ? 'warning' : 'secondary'}
                                  size="sm"
                                  onClick={() => handleToggleItemStatus(item)}
                                  disabled={!canEdit}
                                  title={`Текущий статус: ${item.status === 'active' ? 'Активно' : item.status === 'coming_soon' ? 'Скоро' : 'Скрыто'}. Нажмите для изменения`}
                                >
                                  <i className={`fas ${item.status === 'active' ? 'fa-check-circle' : item.status === 'coming_soon' ? 'fa-clock' : 'fa-eye-slash'}`}></i>
                                </Button>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                    onClick={() => openEditModal(item)}
                                  disabled={!canEdit}
                                >
                                    Редактировать
                                </Button>
                            <Button
                                    variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteItem(item._id)}
                              disabled={!canEdit}
                            >
                              Удалить
                            </Button>
                                </div>
                              </div>
                        </div>
                      ))}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </Card.Body>
            </Card>
          )}
          
          {(!menu || !menu.items || menu.items.length === 0) && (
            <Card className="mb-4 w-100">
              <Card.Header className="bg-primary text-white">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <span className="me-2">📋</span>
                    <h5 className="mb-0">Пункты меню по категориям</h5>
                  </div>
                  <Badge bg="light" text="dark" className="px-2 py-1">
                    0 блюд
                  </Badge>
                </div>
              </Card.Header>
              <Card.Body className="text-center text-muted d-flex align-items-center justify-content-center">
                <div>
                  <h6>Пункты меню</h6>
                  <p className="mb-0">Пока нет добавленных блюд</p>
                </div>
              </Card.Body>
            </Card>
          )}
          </Col>
        </Row>
      </Tab>
      
      <Tab eventKey="group-options" title="Группы опций">
        <GroupOptionsManagement 
          restaurantId={restaurant?._id} 
          onGroupOptionsUpdate={fetchGroupOptions}
        />
      </Tab>
      
      <Tab eventKey="categories" title="Категории">
        {menu && (
          <CategoryManagement 
            restaurantId={restaurant._id} 
            menu={menu} 
            onMenuUpdate={handleMenuUpdate} 
          />
        )}
      </Tab>
      </Tabs>

      <Modal show={qrVisible} onHide={() => setQrVisible(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>QR-код этого меню</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {qrUrl ? (
            <div className="text-center">
              <QRCodeCanvas id={qrCanvasId} value={qrUrl} size={256} includeMargin />
              <div className="mt-3">
                <Form.Control type="text" readOnly value={qrUrl} onFocus={(e) => e.target.select()} />
                <div className="d-flex gap-2 mt-2 justify-content-center">
                  <Button variant="outline-secondary" size="sm" onClick={copyQRUrl}>Скопировать ссылку</Button>
                  <Button variant="outline-primary" size="sm" onClick={printQR}>Печать</Button>
                  <Button variant="outline-success" size="sm" onClick={downloadQR}>Скачать PNG</Button>
                </div>
              </div>
            </div>
          ) : (
            <Alert variant="warning" className="mb-0">Ссылка не сформирована</Alert>
          )}
        </Modal.Body>
      </Modal>

      {/* Branding modal */}
      <Modal show={brandingVisible} onHide={() => setBrandingVisible(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Конфигуратор</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!canEdit && (
            <Alert variant="warning" className="mb-3">
              У вас нет прав на изменение брендинга. Обратитесь к владельцу ресторана.
            </Alert>
          )}
          {menus.length === 0 && (
            <Alert variant="warning" className="mb-3">
              <Alert.Heading>Создайте меню для настройки брендинга</Alert.Heading>
              <p className="mb-0">
                Для настройки брендинга и информации о ресторане необходимо сначала создать меню. 
                Перейдите в раздел «Наборы меню» и создайте первое меню.
              </p>
            </Alert>
          )}
          {menus.length > 0 && !activeMenuId && (
            <Alert variant="info" className="mb-3">
              Выберите активное меню в разделе «Наборы меню», чтобы редактировать брендинг именно его.
            </Alert>
          )}

          {menus.length > 0 && (
            <Form>
            

            <Form.Group className="mb-3">
              <Form.Label>Загрузить логотип с компьютера</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleUploadLogo}
                disabled={!canEdit || logoUploading}
              />
              {logoUploading && <div className="text-muted mt-1">Загрузка...</div>}
              {logoError && <div className="text-danger mt-1">{logoError}</div>}
              {brandingForm.logoUrl && (
                <div className="mt-2 text-center">
                  <img
                    src={brandingForm.logoUrl}
                    alt="Предпросмотр логотипа"
                    style={{ maxWidth: '160px', maxHeight: '120px', objectFit: 'contain' }}
                    className="rounded border bg-white p-1"
                  />
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Рекламный блок</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={brandingForm.adText}
                onChange={(e) => setBrandingForm({ ...brandingForm, adText: e.target.value })}
                placeholder="Краткое рекламное сообщение (опционально)"
                disabled={!canEdit}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Текст внизу (футер)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={brandingForm.footerText}
                onChange={(e) => setBrandingForm({ ...brandingForm, footerText: e.target.value })}
                placeholder="Например: Спасибо за ваш визит!"
                disabled={!canEdit}
              />
            </Form.Group>

            {/* Contact Information Section */}
            <div className="mb-3">
              <div 
                className="d-flex align-items-center justify-content-between p-2 border rounded bg-light cursor-pointer"
                onClick={() => toggleSection('contact')}
                style={{ cursor: 'pointer' }}
              >
                <h6 className="mb-0">Контактная информация</h6>
                <span>{expandedSections.contact ? '▼' : '▶'}</span>
              </div>
              
              {expandedSections.contact && (
                <div className="mt-3 p-3 border rounded bg-white">
            <Row className="g-2">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Телефон</Form.Label>
                  <Form.Control
                    type="text"
                    value={brandingForm.contact?.phone || ''}
                    onChange={(e) =>
                      setBrandingForm({
                        ...brandingForm,
                        contact: { ...brandingForm.contact, phone: e.target.value }
                      })
                    }
                          placeholder="+375..."
                    disabled={!canEdit}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={brandingForm.contact?.email || ''}
                    onChange={(e) =>
                      setBrandingForm({
                        ...brandingForm,
                        contact: { ...brandingForm.contact, email: e.target.value }
                      })
                    }
                    placeholder="info@restaurant.com"
                    disabled={!canEdit}
                  />
                </Form.Group>
              </Col>
                    <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Адрес</Form.Label>
              <Form.Control
                type="text"
                value={brandingForm.contact?.address || ''}
                onChange={(e) =>
                  setBrandingForm({
                    ...brandingForm,
                    contact: { ...brandingForm.contact, address: e.target.value }
                  })
                }
                placeholder="Город, улица, дом"
                disabled={!canEdit}
              />
            </Form.Group>
                    </Col>
                    <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>УНП</Form.Label>
              <Form.Control
                type="url"
                value={brandingForm.contact?.website || ''}
                onChange={(e) =>
                  setBrandingForm({
                    ...brandingForm,
                    contact: { ...brandingForm.contact, website: e.target.value }
                  })
                }
                placeholder="УНП000000000"
                disabled={!canEdit}
              />
            </Form.Group>
                    </Col>
                  </Row>
                </div>
              )}
            </div>

            {/* Social Media Section */}
            <div className="mb-3">
              <div 
                className="d-flex align-items-center justify-content-between p-2 border rounded bg-light cursor-pointer"
                onClick={() => toggleSection('social')}
                style={{ cursor: 'pointer' }}
              >
                <h6 className="mb-0">Социальные сети</h6>
                <span>{expandedSections.social ? '▼' : '▶'}</span>
              </div>
              
              {expandedSections.social && (
                <div className="mt-3 p-3 border rounded bg-white">
                  <Row className="g-2">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Instagram</Form.Label>
                    <Form.Control
                      type="text"
                      value={brandingForm.social?.instagram || ''}
                      onChange={(e) =>
                        setBrandingForm({
                          ...brandingForm,
                          social: { ...brandingForm.social, instagram: e.target.value }
                        })
                      }
                      placeholder="@restaurant_name"
                      disabled={!canEdit}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Facebook</Form.Label>
                    <Form.Control
                      type="text"
                      value={brandingForm.social?.facebook || ''}
                      onChange={(e) =>
                        setBrandingForm({
                          ...brandingForm,
                          social: { ...brandingForm.social, facebook: e.target.value }
                        })
                      }
                      placeholder="facebook.com/restaurant"
                      disabled={!canEdit}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="g-2">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Telegram</Form.Label>
                    <Form.Control
                      type="text"
                      value={brandingForm.social?.telegram || ''}
                      onChange={(e) =>
                        setBrandingForm({
                          ...brandingForm,
                          social: { ...brandingForm.social, telegram: e.target.value }
                        })
                      }
                      placeholder="@restaurant_bot"
                      disabled={!canEdit}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>VKontakte</Form.Label>
                    <Form.Control
                      type="text"
                      value={brandingForm.social?.vk || ''}
                      onChange={(e) =>
                        setBrandingForm({
                          ...brandingForm,
                          social: { ...brandingForm.social, vk: e.target.value }
                        })
                      }
                      placeholder="vk.com/restaurant"
                      disabled={!canEdit}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="g-2">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>WhatsApp</Form.Label>
                    <Form.Control
                      type="text"
                      value={brandingForm.social?.whatsapp || ''}
                      onChange={(e) =>
                        setBrandingForm({
                          ...brandingForm,
                          social: { ...brandingForm.social, whatsapp: e.target.value }
                        })
                      }
                      placeholder="+375..."
                      disabled={!canEdit}
                    />
                  </Form.Group>
                </Col>
                  </Row>
                </div>
              )}
            </div>


            {/* Working Hours Section */}
            <div className="mb-3">
              <div 
                className="d-flex align-items-center justify-content-between p-2 border rounded bg-light cursor-pointer"
                onClick={() => toggleSection('workingHours')}
                style={{ cursor: 'pointer' }}
              >
                <h6 className="mb-0">Режим работы</h6>
                <span>{expandedSections.workingHours ? '▼' : '▶'}</span>
              </div>
              
              {expandedSections.workingHours && (
                <div className="mt-3 p-3 border rounded bg-white">
              <Row className="g-2">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Часовой пояс</Form.Label>
                    <Form.Select
                      value={brandingForm.workingHours?.timezone || 'Europe/Minsk'}
                      onChange={(e) =>
                        setBrandingForm({
                          ...brandingForm,
                          workingHours: { ...brandingForm.workingHours, timezone: e.target.value }
                        })
                      }
                      disabled={!canEdit}
                    >
                      <option value="Europe/Minsk">Минск (UTC+3)</option>
                      <option value="Europe/Moscow">Москва (UTC+3)</option>
                      <option value="Europe/Kiev">Киев (UTC+2)</option>
                      <option value="Europe/Warsaw">Варшава (UTC+1)</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="restrict-orders-outside-hours"
                      label="Запретить заказы вне времени работы"
                      checked={brandingForm.workingHours?.restrictOrdersOutsideHours || false}
                      onChange={(e) => {
                        setBrandingForm({
                          ...brandingForm,
                          workingHours: { 
                            ...brandingForm.workingHours, 
                            restrictOrdersOutsideHours: e.target.checked 
                          }
                        });
                      }}
                      disabled={!canEdit}
                    />
                    <Form.Text className="text-muted">
                      Если включено, клиенты не смогут делать заказы когда ресторан закрыт
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day, index) => {
                const dayNames = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];
                const dayData = brandingForm.workingHours?.[day] || { open: '', close: '', closed: false };
                
                return (
                  <Row key={day} className="g-2 mb-2">
                    <Col md={3}>
                      <Form.Check
                        type="checkbox"
                        id={`working-hours-${day}`}
                        label={dayNames[index]}
                        checked={!dayData.closed}
                        onChange={(e) =>
                          setBrandingForm({
                            ...brandingForm,
                            workingHours: {
                              ...brandingForm.workingHours,
                              [day]: { ...dayData, closed: !e.target.checked }
                            }
                          })
                        }
                        disabled={!canEdit}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Control
                        type="time"
                        value={dayData.open}
                        onChange={(e) =>
                          setBrandingForm({
                            ...brandingForm,
                            workingHours: {
                              ...brandingForm.workingHours,
                              [day]: { ...dayData, open: e.target.value }
                            }
                          })
                        }
                        disabled={!canEdit || dayData.closed}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Control
                        type="time"
                        value={dayData.close}
                        onChange={(e) =>
                          setBrandingForm({
                            ...brandingForm,
                            workingHours: {
                              ...brandingForm.workingHours,
                              [day]: { ...dayData, close: e.target.value }
                            }
                          })
                        }
                        disabled={!canEdit || dayData.closed}
                      />
                    </Col>
                    <Col md={3}>
                      {dayData.closed ? (
                        <span className="text-muted">Закрыто</span>
                      ) : (
                        <span className="text-success">Работает</span>
                      )}
                    </Col>
                  </Row>
                );
              })}
                </div>
              )}
            </div>

            {/* Color Theme Section */}
            <div className="mb-3">
              <div 
                className="d-flex align-items-center justify-content-between p-2 border rounded bg-light cursor-pointer"
                onClick={() => toggleSection('theme')}
                style={{ cursor: 'pointer' }}
              >
                <h6 className="mb-0">Цветовая схема</h6>
                <span>{expandedSections.theme ? '▼' : '▶'}</span>
              </div>
              
              {expandedSections.theme && (
                <div className="mt-3 p-3 border rounded bg-white">
                                             
              {/* Card Opacity Section */}
              <Row className="g-2 mt-3">
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Прозрачность карточек: {Math.round((brandingForm.theme?.opacity ?? 0.9) * 100)}%
                    </Form.Label>
                    <Form.Range
                      min="0"
                      max="1"
                      step="0.05"
                      value={brandingForm.theme?.opacity ?? 0.9}
                      onChange={(e) =>
                        setBrandingForm({
                          ...brandingForm,
                          theme: { ...brandingForm.theme, opacity: parseFloat(e.target.value) }
                        })
                      }
                      disabled={!canEdit}
                      className="mb-2"
                    />
                    <div className="d-flex justify-content-between text-muted small mb-2">
                      <span>0% (полностью прозрачно)</span>
                      <span>50% (полупрозрачно)</span>
                      <span>100% (полностью непрозрачно)</span>
                    </div>
                    
                    {/* Quick opacity presets for cards */}
                    <div className="d-flex gap-2 flex-wrap">
                      <span className="text-muted small">Быстрый выбор:</span>
                      {[
                        { label: 'Прозрачно', value: 0 },
                        { label: 'Полупрозрачно', value: 0.7 },
                        { label: 'Обычно', value: 0.9 },
                        { label: 'Непрозрачно', value: 1.0 }
                      ].map((preset) => (
                        <button
                          key={preset.value}
                          type="button"
                          className={`btn btn-sm ${(brandingForm.theme?.opacity ?? 0.9) === preset.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() =>
                            setBrandingForm({
                              ...brandingForm,
                              theme: { ...brandingForm.theme, opacity: preset.value }
                            })
                          }
                          disabled={!canEdit}
                          style={{ fontSize: '12px', padding: '2px 8px' }}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Exact value input for card opacity */}
              <Row className="g-2">
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Точное значение прозрачности карточек</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      max="1"
                      step="0.01"
                      value={brandingForm.theme?.opacity ?? 0.9}
                      onChange={(e) =>
                        setBrandingForm({
                          ...brandingForm,
                          theme: { ...brandingForm.theme, opacity: parseFloat(e.target.value) || 0.9 }
                        })
                      }
                      disabled={!canEdit}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Дополнительные цветовые поля */}
              
              <Row className="g-2">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Основной цвет</Form.Label>
                    
                    <div className="d-flex gap-2">
                      <Form.Control
                        type="color"
                        value={brandingForm.theme?.primary || '#007bff'}
                        onChange={(e) =>
                          setBrandingForm({
                            ...brandingForm,
                            theme: { ...brandingForm.theme, primary: e.target.value }
                          })
                        }
                        disabled={!canEdit}
                        style={{ width: '60px', height: '38px' }}
                      />
                      <Form.Control
                        type="text"
                        value={brandingForm.theme?.primary || '#007bff'}
                        onChange={(e) =>
                          setBrandingForm({
                            ...brandingForm,
                            theme: { ...brandingForm.theme, primary: e.target.value }
                          })
                        }
                        disabled={!canEdit}
                        placeholder="#007bff"
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Акцентный цвет</Form.Label>
                    
                    <div className="d-flex gap-2">
                      <Form.Control
                        type="color"
                        value={brandingForm.theme?.success || '#28a745'}
                        onChange={(e) =>
                          setBrandingForm({
                            ...brandingForm,
                            theme: { ...brandingForm.theme, success: e.target.value }
                          })
                        }
                        disabled={!canEdit}
                        style={{ width: '60px', height: '38px' }}
                      />
                      <Form.Control
                        type="text"
                        value={brandingForm.theme?.success || '#28a745'}
                        onChange={(e) =>
                          setBrandingForm({
                            ...brandingForm,
                            theme: { ...brandingForm.theme, success: e.target.value }
                          })
                        }
                        disabled={!canEdit}
                        placeholder="#007bff"
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="g-2">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Фон страницы</Form.Label>
                    <div className="d-flex gap-2">
                      <Form.Control
                        type="color"
                        value={brandingForm.theme?.background || '#ffffff'}
                        onChange={(e) =>
                          setBrandingForm({
                            ...brandingForm,
                            theme: { ...brandingForm.theme, background: e.target.value }
                          })
                        }
                        disabled={!canEdit}
                        style={{ width: '60px', height: '38px' }}
                      />
                      <Form.Control
                        type="text"
                        value={brandingForm.theme?.background || '#ffffff'}
                        onChange={(e) =>
                          setBrandingForm({
                            ...brandingForm,
                            theme: { ...brandingForm.theme, background: e.target.value }
                          })
                        }
                        disabled={!canEdit}
                        placeholder="#ffffff"
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Фон карточек</Form.Label>
                    <div className="d-flex gap-2">
                      <Form.Control
                        type="color"
                        value={brandingForm.theme?.surface || '#ffffff'}
                        onChange={(e) =>
                          setBrandingForm({
                            ...brandingForm,
                            theme: { ...brandingForm.theme, surface: e.target.value }
                          })
                        }
                        disabled={!canEdit}
                        style={{ width: '60px', height: '38px' }}
                      />
                      <Form.Control
                        type="text"
                        value={brandingForm.theme?.surface || '#ffffff'}
                        onChange={(e) =>
                          setBrandingForm({
                            ...brandingForm,
                            theme: { ...brandingForm.theme, surface: e.target.value }
                          })
                        }
                        disabled={!canEdit}
                        placeholder="#ffffff"
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="g-2">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Цвет текста</Form.Label>
                    <div className="d-flex gap-2">
                      <Form.Control
                        type="color"
                        value={brandingForm.theme?.text || '#212529'}
                        onChange={(e) =>
                          setBrandingForm({
                            ...brandingForm,
                            theme: { ...brandingForm.theme, text: e.target.value }
                          })
                        }
                        disabled={!canEdit}
                        style={{ width: '60px', height: '38px' }}
                      />
                      <Form.Control
                        type="text"
                        value={brandingForm.theme?.text || '#212529'}
                        onChange={(e) =>
                          setBrandingForm({
                            ...brandingForm,
                            theme: { ...brandingForm.theme, text: e.target.value }
                          })
                        }
                        disabled={!canEdit}
                        placeholder="#212529"
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Цвет рамок</Form.Label>
                    <div className="d-flex gap-2">
                      <Form.Control
                        type="color"
                        value={brandingForm.theme?.border || '#dee2e6'}
                        onChange={(e) =>
                          setBrandingForm({
                            ...brandingForm,
                            theme: { ...brandingForm.theme, border: e.target.value }
                          })
                        }
                        disabled={!canEdit}
                        style={{ width: '60px', height: '38px' }}
                      />
                      <Form.Control
                        type="text"
                        value={brandingForm.theme?.border || '#dee2e6'}
                        onChange={(e) =>
                          setBrandingForm({
                            ...brandingForm,
                            theme: { ...brandingForm.theme, border: e.target.value }
                          })
                        }
                        disabled={!canEdit}
                        placeholder="#dee2e6"
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Кнопка сброса к значениям по умолчанию */}
              <Row className="mt-3">
                <Col md={12}>
                  <div className="d-flex justify-content-end">
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={resetColorsToDefault}
                      disabled={!canEdit}
                    >
                      По умолчанию
                    </Button>
                  </div>
                </Col>
              </Row>
              
              <div className="border rounded p-3 bg-white">
                  <div 
                    className="preview-container"
                    style={{
                      backgroundColor: brandingForm.theme?.background || '#ffffff',
                      backgroundImage: brandingForm.backgroundImage?.url ? `url(${brandingForm.backgroundImage.url})` : 'none',
                      backgroundPosition: brandingForm.backgroundImage?.position || 'center',
                      backgroundSize: brandingForm.backgroundImage?.size || 'cover',
                      backgroundRepeat: brandingForm.backgroundImage?.repeat || 'no-repeat',
                      backgroundOpacity: brandingForm.backgroundImage?.opacity || 0.1,
                      minHeight: '400px',
                      padding: '20px',
                      borderRadius: '8px',
                      position: 'relative'
                    }}
                  >
                    {/* Логотип */}
                    {brandingForm.logoUrl && (
                      <div className="text-center mb-3">
                        <img 
                          src={brandingForm.logoUrl} 
                          alt="Логотип" 
                          style={{ 
                            maxHeight: '60px', 
                            maxWidth: '200px',
                            objectFit: 'contain'
                          }}
                        />
                      </div>
                    )}
                    
                    {/* Заголовок */}
                    <h4 
                      className="text-center mb-3"
                      style={{ 
                        color: brandingForm.theme?.primary || '#007bff',
                        fontWeight: 'bold'
                      }}
                    >
                      Меню ресторана
                    </h4>
                    
                    {/* Карточка товара */}
                    <div 
                      className="card mb-3"
                      style={{
                        backgroundColor: brandingForm.theme?.surface || '#ffffff',
                        borderColor: brandingForm.theme?.border || '#dee2e6',
                        opacity: brandingForm.theme?.opacity || 0.9,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    >
                      <div className="card-body">
                        <h5 
                          className="card-title"
                          style={{ color: brandingForm.theme?.primary || '#212529' }}
                        >
                          Пример блюда
                        </h5>
                        <p 
                          className="card-text"
                          style={{ color: brandingForm.theme?.text || '#212529' }}
                        >
                          Описание блюда с ингредиентами и особенностями приготовления.
                        </p>
                        <div className="d-flex justify-content-between align-items-center">
                          <span 
                            className="fw-bold"
                            style={{ color: brandingForm.theme?.success || '#28a745' }}
                          >
                            15.50 BYN
                          </span>
                          <button 
                            className="btn btn-primary btn-sm"
                            style={{
                              backgroundColor: brandingForm.theme?.success || '#007bff',
                              borderColor: brandingForm.theme?.success || '#007bff'
                            }}
                          
                          >
                            В корзину
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Ссылка */}
                    <div className="text-center mb-3">
                      <a 
                        href="#"
                        style={{ color: brandingForm.theme?.primary || '#007bff' }}
                      >
                        Подробнее о ресторане
                      </a>
                    </div>
                                                    
                    {/* Футер */}
                    {brandingForm.footerText && (
                      <div 
                        className="text-center mt-3 pt-3 border-top"
                        style={{ 
                          color: brandingForm.theme?.text || '#212529',
                          borderColor: brandingForm.theme?.border || '#dee2e6'
                        }}
                      >
                        {brandingForm.footerText}
                      </div>
                    )}
                    
                    {/* Реклама */}
                    {brandingForm.adText && (
                      <div 
                        className="text-center mt-3 p-2 rounded"
                        style={{ 
                          backgroundColor: brandingForm.theme?.primary || '#007bff',
                          color: '#ffffff'
                        }}
                      >
                        {brandingForm.adText}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              )}
            </div>

            {/* Background Image Section */}
            <div className="mb-3">
              <div 
                className="d-flex align-items-center justify-content-between p-2 border rounded bg-light cursor-pointer"
                onClick={() => toggleSection('backgroundImage')}
                style={{ cursor: 'pointer' }}
              >
                <h6 className="mb-0">Фоновое изображение</h6>
                <span>{expandedSections.backgroundImage ? '▼' : '▶'}</span>
              </div>
              
              {expandedSections.backgroundImage && (
                <div className="mt-3 p-3 border rounded bg-white">
              <Form.Group className="mb-3">
                <Form.Label>URL фонового изображения</Form.Label>
                <Form.Control
                  type="url"
                  value={brandingForm.backgroundImage?.url || ''}
                  onChange={(e) =>
                    setBrandingForm({
                      ...brandingForm,
                      backgroundImage: { ...brandingForm.backgroundImage, url: e.target.value }
                    })
                  }
                  placeholder="https://example.com/background.jpg"
                  disabled={!canEdit}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Загрузить фоновое изображение с компьютера</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files && e.target.files[0];
                    if (!file) return;
                    try {
                      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('managerToken');
                      if (!token) {
                        setError('Токен не найден. Пожалуйста, войдите снова.');
                        return;
                      }
                      const formData = new FormData();
                      formData.append('image', file);
                      const res = await axios.post('/api/upload/image', formData, {
                        headers: { 'x-auth-token': token, 'Content-Type': 'multipart/form-data' }
                      });
                      
                      console.log('Background image upload response:', res.data);
                      
                      setBrandingForm({
                        ...brandingForm,
                        backgroundImage: { ...brandingForm.backgroundImage, url: res.data.url }
                      });
                      setSuccess('Фоновое изображение загружено');
                    } catch (err) {
                      setError(err.response?.data?.message || err.message || 'Ошибка загрузки изображения');
                    }
                  }}
                  disabled={!canEdit}
                />
              </Form.Group>

              {brandingForm.backgroundImage?.url && (
                <div className="mb-3">
                  <img
                    src={brandingForm.backgroundImage.url}
                    alt="Предпросмотр фонового изображения"
                    style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'contain' }}
                    className="rounded border bg-white p-1"
                  />
                </div>
              )}

              <Row className="g-2">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Прозрачность: {Math.round((brandingForm.backgroundImage?.opacity ?? 0.1) * 100)}%
                    </Form.Label>
                    <Form.Range
                      min="0"
                      max="1"
                      step="0.05"
                      value={brandingForm.backgroundImage?.opacity ?? 0.1}
                      onChange={(e) =>
                        setBrandingForm({
                          ...brandingForm,
                          backgroundImage: { ...brandingForm.backgroundImage, opacity: parseFloat(e.target.value) }
                        })
                      }
                      disabled={!canEdit}
                      className="mb-2"
                    />
                    <div className="d-flex justify-content-between text-muted small mb-2">
                      <span>0% (невидимо)</span>
                      <span>50% (полупрозрачно)</span>
                      <span>100% (полностью видимо)</span>
                    </div>
                    
                    {/* Quick opacity presets */}
                    <div className="d-flex gap-2 flex-wrap">
                      <span className="text-muted small">Быстрый выбор:</span>
                      {[
                        { label: 'Слабо', value: 0.1 },
                        { label: 'Средне', value: 0.3 },
                        { label: 'Сильно', value: 0.5 },
                        { label: 'Очень сильно', value: 0.7 }
                      ].map((preset) => (
                        <button
                          key={preset.value}
                          type="button"
                          className={`btn btn-sm ${(brandingForm.backgroundImage?.opacity ?? 0.1) === preset.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() =>
                            setBrandingForm({
                              ...brandingForm,
                              backgroundImage: { ...brandingForm.backgroundImage, opacity: preset.value }
                            })
                          }
                          disabled={!canEdit}
                          style={{ fontSize: '12px', padding: '2px 8px' }}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Точное значение</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      max="1"
                      step="0.01"
                      value={brandingForm.backgroundImage?.opacity ?? 0.1}
                      onChange={(e) =>
                        setBrandingForm({
                          ...brandingForm,
                          backgroundImage: { ...brandingForm.backgroundImage, opacity: parseFloat(e.target.value) || 0.1 }
                        })
                      }
                      disabled={!canEdit}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Preview of background image with opacity */}
              {brandingForm.backgroundImage?.url && (
                <div className="mb-3">
                  <Form.Label>Предпросмотр с прозрачностью:</Form.Label>
                  <div 
                    className="border rounded p-3"
                    style={{
                      backgroundImage: `url("${brandingForm.backgroundImage.url}")`,
                      backgroundPosition: brandingForm.backgroundImage.position || 'center',
                      backgroundSize: brandingForm.backgroundImage.size || 'cover',
                      backgroundRepeat: brandingForm.backgroundImage.repeat || 'no-repeat',
                      minHeight: '150px',
                      position: 'relative'
                    }}
                  >
                    <div 
                      className="rounded p-3"
                      style={{
                        backgroundColor: `rgba(255, 255, 255, ${1 - (brandingForm.backgroundImage?.opacity || 0.1)})`,
                        position: 'absolute',
                        top: '10px',
                        left: '10px',
                        right: '10px',
                        bottom: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: '#333'
                      }}
                    >
                      Пример текста поверх фона
                    </div>
                  </div>
                </div>
              )}
              
              <Row className="g-2">
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Позиция</Form.Label>
                    <Form.Select
                      value={brandingForm.backgroundImage?.position || 'center'}
                      onChange={(e) =>
                        setBrandingForm({
                          ...brandingForm,
                          backgroundImage: { ...brandingForm.backgroundImage, position: e.target.value }
                        })
                      }
                      disabled={!canEdit}
                    >
                      <option value="center">Центр</option>
                      <option value="top">Верх</option>
                      <option value="bottom">Низ</option>
                      <option value="left">Лево</option>
                      <option value="right">Право</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Размер</Form.Label>
                    <Form.Select
                      value={brandingForm.backgroundImage?.size || 'cover'}
                      onChange={(e) =>
                        setBrandingForm({
                          ...brandingForm,
                          backgroundImage: { ...brandingForm.backgroundImage, size: e.target.value }
                        })
                      }
                      disabled={!canEdit}
                    >
                      <option value="cover">Покрыть</option>
                      <option value="contain">Вместить</option>
                      <option value="auto">Авто</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Повторение</Form.Label>
                    <Form.Select
                      value={brandingForm.backgroundImage?.repeat || 'no-repeat'}
                      onChange={(e) =>
                        setBrandingForm({
                          ...brandingForm,
                          backgroundImage: { ...brandingForm.backgroundImage, repeat: e.target.value }
                        })
                      }
                      disabled={!canEdit}
                    >
                      <option value="no-repeat">Не повторять</option>
                      <option value="repeat">Повторять</option>
                      <option value="repeat-x">Повторять по горизонтали</option>
                      <option value="repeat-y">Повторять по вертикали</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
                </div>
              )}
            </div>

            {/* Slideshow Section */}
            <div className="mb-3">
              <div 
                className="d-flex align-items-center justify-content-between p-2 border rounded bg-light cursor-pointer"
                onClick={() => toggleSection('slideshow')}
                style={{ cursor: 'pointer' }}
              >
                <h6 className="mb-0">Рекламный Слайдер</h6>
                <span>{expandedSections.slideshow ? '▼' : '▶'}</span>
              </div>
              
              {expandedSections.slideshow && (
                <div className="mt-3 p-3 border rounded bg-white">
                  <Row className="g-2">
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="switch"
                          id="slideshow-enabled"
                          label="Включить слайдшоу"
                          checked={brandingForm.slideshow?.enabled || false}
                          onChange={(e) =>
                            setBrandingForm({
                              ...brandingForm,
                              slideshow: { 
                                ...brandingForm.slideshow, 
                                enabled: e.target.checked 
                              }
                            })
                          }
                          disabled={!canEdit}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  {brandingForm.slideshow?.enabled && (
                    <>
                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Интервал смены слайдов (мс)</Form.Label>
                            <Form.Control
                              type="number"
                              min="1000"
                              step="500"
                              value={brandingForm.slideshow?.interval || 3000}
                              onChange={(e) =>
                                setBrandingForm({
                                  ...brandingForm,
                                  slideshow: { 
                                    ...brandingForm.slideshow, 
                                    interval: parseInt(e.target.value) || 3000 
                                  }
                                })
                              }
                              disabled={!canEdit}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Check
                              type="switch"
                              id="slideshow-autoplay"
                              label="Автопрокрутка"
                              checked={brandingForm.slideshow?.autoplay || true}
                              onChange={(e) =>
                                setBrandingForm({
                                  ...brandingForm,
                                  slideshow: { 
                                    ...brandingForm.slideshow, 
                                    autoplay: e.target.checked 
                                  }
                                })
                              }
                              disabled={!canEdit}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Check
                              type="switch"
                              id="slideshow-indicators"
                              label="Показывать индикаторы"
                              checked={brandingForm.slideshow?.showIndicators || true}
                              onChange={(e) =>
                                setBrandingForm({
                                  ...brandingForm,
                                  slideshow: { 
                                    ...brandingForm.slideshow, 
                                    showIndicators: e.target.checked 
                                  }
                                })
                              }
                              disabled={!canEdit}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Check
                              type="switch"
                              id="slideshow-arrows"
                              label="Показывать стрелки"
                              checked={brandingForm.slideshow?.showArrows || true}
                              onChange={(e) =>
                                setBrandingForm({
                                  ...brandingForm,
                                  slideshow: { 
                                    ...brandingForm.slideshow, 
                                    showArrows: e.target.checked 
                                  }
                                })
                              }
                              disabled={!canEdit}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row className="g-3">
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label>Изображения слайдшоу</Form.Label>
                            <div className="border rounded p-3" style={{ minHeight: '200px' }}>
                              {brandingForm.slideshow?.images?.length > 0 ? (
                                <div className="row g-2">
                                  {brandingForm.slideshow.images.map((image, index) => (
                                    <div key={index} className="col-md-4">
                                      <div className="position-relative">
                                        <img
                                          src={image.url}
                                          alt={image.alt || `Слайд ${index + 1}`}
                                          className="img-fluid rounded"
                                          style={{ width: '100%', height: '120px', objectFit: 'cover' }}
                                        />
                                        <Button
                                          variant="danger"
                                          size="sm"
                                          className="position-absolute top-0 end-0 m-1"
                                          onClick={() => {
                                            const newImages = [...brandingForm.slideshow.images];
                                            newImages.splice(index, 1);
                                            setBrandingForm({
                                              ...brandingForm,
                                              slideshow: { 
                                                ...brandingForm.slideshow, 
                                                images: newImages 
                                              }
                                            });
                                          }}
                                          disabled={!canEdit}
                                        >
                                          ×
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-center text-muted py-4">
                                  <p>Нет изображений для слайдшоу</p>
                                  <p className="small">Добавьте изображения через загрузку</p>
                                </div>
                              )}
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Добавить изображение по URL</Form.Label>
                            <Form.Control
                              type="url"
                              placeholder="URL изображения"
                              onChange={async (e) => {
                                const url = e.target.value.trim();
                                if (url) {
                                  try {
                                    const newImages = [...(brandingForm.slideshow?.images || [])];
                                    newImages.push({
                                      url: url,
                                      alt: `Слайд ${newImages.length + 1}`,
                                      order: newImages.length
                                    });
                                    setBrandingForm({
                                      ...brandingForm,
                                      slideshow: { 
                                        ...brandingForm.slideshow, 
                                        images: newImages 
                                      }
                                    });
                                    e.target.value = '';
                                  } catch (err) {
                                    console.error('Error adding image:', err);
                                  }
                                }
                              }}
                              disabled={!canEdit}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Загрузить с устройства</Form.Label>
                            <Form.Control
                              type="file"
                              accept="image/*"
                              onChange={async (e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  try {
                                    const token = localStorage.getItem('restaurantToken') || localStorage.getItem('managerToken');
                                    if (!token) {
                                      setError('Токен не найден. Пожалуйста, войдите снова.');
                                      return;
                                    }
                                    
                                    const formData = new FormData();
                                    formData.append('image', file);
                                    const res = await axios.post('/api/upload/image', formData, {
                                      headers: { 'x-auth-token': token, 'Content-Type': 'multipart/form-data' }
                                    });
                                    
                                    const newImages = [...(brandingForm.slideshow?.images || [])];
                                    newImages.push({
                                      url: res.data.url,
                                      alt: `Слайд ${newImages.length + 1}`,
                                      order: newImages.length
                                    });
                                    setBrandingForm({
                                      ...brandingForm,
                                      slideshow: { 
                                        ...brandingForm.slideshow, 
                                        images: newImages 
                                      }
                                    });
                                    
                                    e.target.value = ''; // Очищаем input
                                    setSuccess('Изображение загружено');
                                  } catch (err) {
                                    console.error('Error uploading image:', err);
                                    setError(err.response?.data?.message || err.message || 'Ошибка загрузки изображения');
                                  }
                                }
                              }}
                              disabled={!canEdit}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </>
                  )}
                </div>
              )}
            </div>
          </Form>
          )}

          {/* Default Menu Section */}
          <div className="mb-3">
            <div 
              className="d-flex align-items-center justify-content-between p-2 border rounded bg-light cursor-pointer"
              onClick={() => toggleSection('defaultMenu')}
              style={{ cursor: 'pointer' }}
            >
              <h6 className="mb-0">Меню по умолчанию для субдомена</h6>
              <span>{expandedSections.defaultMenu ? '▼' : '▶'}</span>
            </div>
            
            {expandedSections.defaultMenu && (
              <div className="mt-3 p-3 border rounded bg-white">
                {!restaurant?.subdomain && (
                  <Alert variant="info" className="mb-3">
                    У вашего ресторана нет субдомена. Обратитесь к администратору для настройки субдомена.
                  </Alert>
                )}
                
                {restaurant?.subdomain && (
                  <>
                    <div className="mb-3">
                      <p className="text-muted">
                        Выберите меню, которое будет отображаться по умолчанию на{' '}
                        <strong>{restaurant.subdomain}.menu.qr-code.by</strong>
                      </p>
                    </div>

                    <Form.Group className="mb-3">
                      <Form.Label>Меню по умолчанию</Form.Label>
                      <Form.Select
                        value={defaultMenuId}
                        onChange={(e) => setDefaultMenuId(e.target.value)}
                        disabled={!canEdit || menus.length === 0}
                      >
                        <option value="">Не выбрано (будет использоваться первое доступное)</option>
                        {menus.map((m) => (
                          <option key={m._id} value={m._id}>
                            {m.title} ({m.mode === 'delivery' ? 'Доставка' : 'В зале'})
                          </option>
                        ))}
                      </Form.Select>
                      {menus.length === 0 && (
                        <Form.Text className="text-muted">
                          Сначала создайте меню в разделе «Наборы меню»
                        </Form.Text>
                      )}
                    </Form.Group>

                    {defaultMenuId && (
                      <Alert variant="info" className="mb-3">
                        <strong>Текущее меню по умолчанию:</strong>{' '}
                        {menus.find(m => m._id === defaultMenuId)?.title || 'Не найдено'}
                      </Alert>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Restaurant Icons and Title Section */}
          <div className="mb-3">
            <div 
              className="d-flex align-items-center justify-content-between p-2 border rounded bg-light cursor-pointer"
              onClick={() => toggleSection('restaurantIcons')}
              style={{ cursor: 'pointer' }}
            >
              <h6 className="mb-0">Favicon и название вкладки браузера</h6>
              <span>{expandedSections.restaurantIcons ? '▼' : '▶'}</span>
            </div>
          
            {expandedSections.restaurantIcons && (
              <div className="mt-3 p-3 border rounded bg-white">
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Название страницы (Title)</Form.Label>
                    <Form.Control
                      type="text"
                      value={restaurantBranding.pageTitle}
                      onChange={(e) => setRestaurantBranding({ ...restaurantBranding, pageTitle: e.target.value })}
                      placeholder="Например: Кафе Фиеста - Меню"
                      disabled={!canEdit}
                    />
                    <Form.Text className="text-muted">
                      Это название будет отображаться во вкладке браузера
                    </Form.Text>
                  </Form.Group>

                  <hr />

                  <h6 className="mb-3">Иконки</h6>

                  {/* Master Icon Upload - Auto Generate All Sizes */}
                  <Form.Group className="mb-4">
                    <Form.Label>
                      <strong>Загрузить одну иконку (автоматическое создание всех размеров)</strong>
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handleUploadMasterIcon}
                      disabled={!canEdit || savingRestaurantBranding}
                    />
                    <Form.Text className="text-muted">
                      Рекомендуется загрузить иконку размером 512x512 пикселей или больше. 
                      Система автоматически создаст все необходимые размеры: 32x32 (favicon), 180x180 (Apple), 192x192 (PWA), 512x512 (PWA)
                    </Form.Text>
                    {savingRestaurantBranding && (
                      <div className="text-info mt-2">
                        <small>⏳ Создание иконок всех размеров...</small>
                      </div>
                    )}
                    {/* Preview of all generated icons */}
                    {(restaurantBranding.faviconUrl || restaurantBranding.appleTouchIconUrl || 
                      restaurantBranding.pwaIcon192Url || restaurantBranding.pwaIcon512Url) && (
                      <div className="mt-3 p-3 border rounded bg-light">
                        <h6 className="mb-2">Созданные иконки:</h6>
                        <div className="d-flex flex-wrap gap-3 align-items-center">
                          {restaurantBranding.faviconUrl && (
                            <div className="text-center">
                              <div className="small text-muted mb-1">Favicon 32x32</div>
                              <img
                                src={restaurantBranding.faviconUrl}
                                alt="Favicon"
                                style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                                className="border rounded p-1 bg-white"
                              />
                            </div>
                          )}
                          {restaurantBranding.appleTouchIconUrl && (
                            <div className="text-center">
                              <div className="small text-muted mb-1">Apple 180x180</div>
                              <img
                                src={restaurantBranding.appleTouchIconUrl}
                                alt="Apple Touch Icon"
                                style={{ width: '64px', height: '64px', objectFit: 'contain' }}
                                className="border rounded p-1 bg-white"
                              />
                            </div>
                          )}
                          {restaurantBranding.pwaIcon192Url && (
                            <div className="text-center">
                              <div className="small text-muted mb-1">PWA 192x192</div>
                              <img
                                src={restaurantBranding.pwaIcon192Url}
                                alt="PWA Icon 192"
                                style={{ width: '64px', height: '64px', objectFit: 'contain' }}
                                className="border rounded p-1 bg-white"
                              />
                            </div>
                          )}
                          {restaurantBranding.pwaIcon512Url && (
                            <div className="text-center">
                              <div className="small text-muted mb-1">PWA 512x512</div>
                              <img
                                src={restaurantBranding.pwaIcon512Url}
                                alt="PWA Icon 512"
                                style={{ width: '64px', height: '64px', objectFit: 'contain' }}
                                className="border rounded p-1 bg-white"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </Form.Group>

                  <hr className="my-4" />
                  <p className="text-muted text-center mb-3">
                    <small>Или загрузите иконки отдельно для каждого размера:</small>
                  </p>

                  <Form.Group className="mb-3">
                    <Form.Label>Favicon (16x16 или 32x32)</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleUploadRestaurantIcon(e, 'faviconUrl')}
                      disabled={!canEdit || savingRestaurantBranding}
                    />
                    {restaurantBranding.faviconUrl && (
                      <div className="mt-2">
                        <img
                          src={restaurantBranding.faviconUrl}
                          alt="Favicon"
                          style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                          className="border rounded p-1 bg-white"
                        />
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Apple Touch Icon (180x180)</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleUploadRestaurantIcon(e, 'appleTouchIconUrl')}
                      disabled={!canEdit || savingRestaurantBranding}
                    />
                    {restaurantBranding.appleTouchIconUrl && (
                      <div className="mt-2">
                        <img
                          src={restaurantBranding.appleTouchIconUrl}
                          alt="Apple Touch Icon"
                          style={{ width: '180px', height: '180px', objectFit: 'contain' }}
                          className="border rounded p-1 bg-white"
                        />
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>PWA Icon 192x192</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleUploadRestaurantIcon(e, 'pwaIcon192Url')}
                      disabled={!canEdit || savingRestaurantBranding}
                    />
                    {restaurantBranding.pwaIcon192Url && (
                      <div className="mt-2">
                        <img
                          src={restaurantBranding.pwaIcon192Url}
                          alt="PWA Icon 192"
                          style={{ width: '192px', height: '192px', objectFit: 'contain' }}
                          className="border rounded p-1 bg-white"
                        />
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>PWA Icon 512x512</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleUploadRestaurantIcon(e, 'pwaIcon512Url')}
                      disabled={!canEdit || savingRestaurantBranding}
                    />
                    {restaurantBranding.pwaIcon512Url && (
                      <div className="mt-2">
                        <img
                          src={restaurantBranding.pwaIcon512Url}
                          alt="PWA Icon 512"
                          style={{ width: '512px', maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
                          className="border rounded p-1 bg-white"
                        />
                      </div>
                    )}
                  </Form.Group>
                </Form>
              </div>
            )}
          </div>
        
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setBrandingVisible(false)}>Закрыть</Button>
          {menus.length > 0 && (
            <Button
              variant="success"
              onClick={async () => {
                try {
                  // Сохраняем брендинг меню
                  if (menus.length > 0 && activeMenuId) {
                    await handleSaveBranding();
                  }
                  // Сохраняем меню по умолчанию (если есть субдомен)
                  if (restaurant?.subdomain && canEdit) {
                    await handleSaveDefaultMenu();
                  }
                  // Сохраняем настройки иконок и title
                  await handleSaveRestaurantBranding();
                  // Закрываем модальное окно после успешного сохранения
                  setBrandingVisible(false);
                } catch (err) {
                  console.error('Error saving branding settings:', err);
                  // Ошибка уже обработана в соответствующих функциях
                }
              }}
              disabled={!canEdit || (menus.length > 0 && !activeMenuId) || savingRestaurantBranding}
            >
              {savingRestaurantBranding ? 'Сохранение...' : 'Сохранить все'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Edit Item Modal */}
      <Modal show={editModalVisible} onHide={() => setEditModalVisible(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Редактировать блюдо</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingItem && editItemMap[editingItem._id] && (
            <div>
              <Row className="g-2">
                <Col md={6}>
                  <Form.Label className="mb-1">Название</Form.Label>
                  <Form.Control
                    size="sm"
                    value={editItemMap[editingItem._id].name}
                    onChange={(e) => onEditItemChange(editingItem._id, 'name', e.target.value)}
                    disabled={!canEdit}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label className="mb-1">Категория</Form.Label>
                  {menu && menu.categories && menu.categories.length > 0 ? (
                    <Form.Select
                      size="sm"
                      value={editItemMap[editingItem._id].category}
                      onChange={(e) => onEditItemChange(editingItem._id, 'category', e.target.value)}
                      disabled={!canEdit}
                    >
                      {menu.categories.map((c, idx) => (
                        <option key={idx} value={c.name}>{c.name}</option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control
                      size="sm"
                      value={editItemMap[editingItem._id].category}
                      onChange={(e) => onEditItemChange(editingItem._id, 'category', e.target.value)}
                      disabled={!canEdit}
                    />
                  )}
                </Col>
              </Row>
              
              <Row className="g-2 mt-2">
                <Col md={8}>
                  <Form.Label className="mb-1">Описание</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={editItemMap[editingItem._id].description}
                    onChange={(e) => onEditItemChange(editingItem._id, 'description', e.target.value)}
                    disabled={!canEdit}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label className="mb-1">Цена (BYN)</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    step="0.01"
                    value={editItemMap[editingItem._id].price}
                    onChange={(e) => onEditItemChange(editingItem._id, 'price', parseFloat(e.target.value) || 0)}
                    disabled={!canEdit}
                  />
                </Col>
              </Row>
              
              <Row className="g-2 mt-2">
                <Col md={6}>
                  <Form.Label className="mb-1">Вес</Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    value={editItemMap[editingItem._id].weight || ''}
                    onChange={(e) => onEditItemChange(editingItem._id, 'weight', e.target.value)}
                    disabled={!canEdit}
                    placeholder="250 или 120/10/5"
                  />
                </Col>
                <Col md={6}>
                  <Form.Label className="mb-1">Ед. изм.</Form.Label>
                  <Form.Select
                    size="sm"
                    value={editItemMap[editingItem._id].weightUnit || 'г'}
                    onChange={(e) => onEditItemChange(editingItem._id, 'weightUnit', e.target.value)}
                    disabled={!canEdit}
                  >
                    <option value="г">г</option>
                    <option value="кг">кг</option>
                    <option value="мл">мл</option>
                    <option value="л">л</option>
                  </Form.Select>
                </Col>
              </Row>
              
              <Row className="g-2 mt-2">
                <Col md={12}>
                  <Form.Label className="mb-1">URL изображения</Form.Label>
                  <Form.Control
                    size="sm"
                    type="url"
                    value={editItemMap[editingItem._id].image}
                    onChange={(e) => onEditItemChange(editingItem._id, 'image', e.target.value)}
                    disabled={!canEdit}
                  />
                </Col>
              </Row>

              {/* Загрузка изображения с компьютера */}
              <Row className="g-2 mt-2">
                <Col md={12}>
                  <Form.Label className="mb-1">Или загрузить с компьютера</Form.Label>
                  <Form.Control
                    size="sm"
                    type="file"
                    accept="image/*"
                    onChange={handleEditImageUpload}
                    disabled={!canEdit || uploading}
                  />
                  {uploading && (
                    <div className="mt-2">
                      <div className="loading-spinner mx-auto"></div>
                      <small className="text-muted d-block text-center mt-1">
                        Загружаем изображение...
                      </small>
                    </div>
                  )}
                  {uploadError && (
                    <div className="text-danger mt-1">{uploadError}</div>
                  )}
                  <Form.Text className="text-muted">
                    Поддерживаются форматы: JPG, PNG, GIF. Максимальный размер: 5MB
                  </Form.Text>
                </Col>
              </Row>

              {/* Превью изображения */}
              {editItemMap[editingItem._id].image && (
                <Row className="g-2 mt-2">
                  <Col md={12}>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <Form.Label className="mb-0">Превью изображения</Form.Label>
                      {canEdit && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => onEditItemChange(editingItem._id, 'image', '')}
                          style={{ fontSize: '0.75rem' }}
                        >
                          Удалить
                        </Button>
                      )}
                    </div>
                    <div className="border rounded p-2" style={{ maxWidth: '200px' }}>
                      <img 
                        src={editItemMap[editingItem._id].image} 
                        alt="Превью" 
                        style={{ 
                          width: '100%', 
                          height: '120px', 
                          objectFit: 'cover',
                          borderRadius: '4px'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div 
                        className="text-muted text-center p-3" 
                        style={{ 
                          display: 'none',
                          fontSize: '0.9rem'
                        }}
                      >
                        Изображение не загружается
                      </div>
                    </div>
                  </Col>
                </Row>
              )}

              {/* Predefined Group Options Selection */}
              {groupOptions.length > 0 && (
                <div className="mt-3">
                  <div className="mb-3 p-3 border rounded bg-light">
                    <div 
                      className="d-flex justify-content-between align-items-center mb-3"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setEditGroupOptionsExpanded(!editGroupOptionsExpanded)}
                    >
                      <Form.Label className="fw-bold mb-0">🔧 Предопределенные группы опций</Form.Label>
                      <span className={`fs-4 transition-all ${editGroupOptionsExpanded ? 'rotate-180' : ''}`}>
                        {editGroupOptionsExpanded ? '▼' : '▶'}
                      </span>
                    </div>
                    
                    {editGroupOptionsExpanded && (
                      <Tabs defaultActiveKey={groupOptions[0]?._id} className="mb-3" id="edit-group-options-tabs">
                        {groupOptions.map((group) => (
                          <Tab 
                            key={group._id} 
                            eventKey={group._id} 
                            title={
                              <div className="d-flex align-items-center gap-2">
                                <span>{group.name}</span>
                                {editItemMap[editingItem._id]?.groupOptionIds?.includes(group._id) && (
                                  <span className="badge bg-success">✓</span>
                                )}
                              </div>
                            }
                          >
                            <div className="p-3">
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                  <h6 className="mb-1">{group.name}</h6>
                                  {group.description && (
                                    <small className="text-muted">{group.description}</small>
                                  )}
                                </div>
                                <Form.Check
                                  type="switch"
                                  id={`edit-group-switch-${editingItem._id}-${group._id}`}
                                  label="Включить группу"
                                  checked={editItemMap[editingItem._id]?.groupOptionIds?.includes(group._id) || false}
                                  onChange={(e) => {
                                    const currentIds = editItemMap[editingItem._id]?.groupOptionIds || [];
                                    if (e.target.checked) {
                                      onEditItemChange(editingItem._id, 'groupOptionIds', [...currentIds, group._id]);
                                    } else {
                                      onEditItemChange(editingItem._id, 'groupOptionIds', currentIds.filter(id => id !== group._id));
                                    }
                                  }}
                                  disabled={!canEdit}
                                />
                              </div>
                              
                              {group.options && group.options.length > 0 && (
                                <div>
                                  <h6 className="mb-2">Опции в группе:</h6>
                                  <div className="row g-2">
                                    {group.options.map((option, idx) => (
                                      <div key={idx} className="col-md-6">
                                        <div className="card border-0 bg-white p-2">
                                          <div className="d-flex justify-content-between align-items-center">
                                            <span className="fw-semibold">{option.name}</span>
                                            <span className="badge bg-primary">
                                              {option.price > 0 ? `+${option.price} BYN` : 'Бесплатно'}
                                            </span>
                                          </div>
                                          {option.isRequired && (
                                            <small className="text-warning">Обязательно</small>
                                          )}
                                          {option.isMultiple && (
                                            <small className="text-info">Можно выбрать несколько</small>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </Tab>
                        ))}
                      </Tabs>
                    )}
                  </div>
                </div>
              )}

              {/* Дополнения в редактировании */}
              <div className="mt-3">
                <div className="mb-3 p-3 border rounded bg-light">
                  <div 
                    className="d-flex justify-content-between align-items-center mb-3"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setEditAddonsExpanded(!editAddonsExpanded)}
                  >
                    <Form.Label className="fw-bold mb-0">➕ Дополнения</Form.Label>
                    <span className={`fs-4 transition-all ${editAddonsExpanded ? 'rotate-180' : ''}`}>
                      {editAddonsExpanded ? '▼' : '▶'}
                    </span>
                  </div>
                  
                  {editAddonsExpanded && (
                    <Tabs defaultActiveKey="simple-addons" className="mb-3" id="edit-addons-tabs">
                      {/* Простые дополнения */}
                      <Tab eventKey="simple-addons" title="Простые дополнения">
                        <div className="p-3">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">Простые дополнения</h6>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => addEditAddonRow(editingItem._id)}
                            >
                              ➕ Добавить
                            </Button>
                          </div>
                          
                          {(editItemMap[editingItem._id]?.addons || []).map((a, idx) => (
                            <div key={idx} className="card border-0 bg-white p-3 mb-2">
                              <Row className="g-2 align-items-center">
                                <Col md={6}>
                                  <Form.Control
                                    size="sm"
                                    placeholder="Название (например, сыр)"
                                    value={a.name}
                                    onChange={(e) => updateEditAddonField(editingItem._id, idx, 'name', e.target.value)}
                                    disabled={!canEdit}
                                  />
                                </Col>
                                <Col md={4}>
                                  <Form.Control
                                    size="sm"
                                    type="number"
                                    step="0.01"
                                    placeholder="Цена (BYN)"
                                    value={a.price}
                                    onChange={(e) => updateEditAddonField(editingItem._id, idx, 'price', parseFloat(e.target.value) || 0)}
                                    disabled={!canEdit}
                                  />
                                </Col>
                                <Col md={2} className="text-end">
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => removeEditAddonRow(editingItem._id, idx)}
                                    disabled={!canEdit}
                                  >
                                    Удалить
                                  </Button>
                                </Col>
                              </Row>
                            </div>
                          ))}
                          
                          {(!editItemMap[editingItem._id]?.addons || editItemMap[editingItem._id].addons.length === 0) && (
                            <div className="text-center text-muted py-4">
                              <span className="fs-4"></span>
                              <p className="mb-0">Пока нет дополнений</p>
                              <small>Нажмите "Добавить" чтобы создать первое дополнение</small>
                            </div>
                          )}
                        </div>
                      </Tab>
                      
                      {/* Группы дополнений */}
                      <Tab eventKey="addon-groups" title="Группы дополнений">
                        <div className="p-3">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">Группы дополнений (радио/чекбокс)</h6>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => addEditGroupRow(editingItem._id)}
                              disabled={!canEdit}
                            >
                              ➕ Добавить группу
                            </Button>
                          </div>
                          
                          {(editItemMap[editingItem._id]?.addonGroups || []).map((g, gidx) => (
                            <div key={gidx} className="card border-0 bg-white p-3 mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">Группа {gidx + 1}</h6>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => removeEditGroupRow(editingItem._id, gidx)}
                                  disabled={!canEdit}
                                >
                                  Удалить Удалить группу
                                </Button>
                              </div>
                              
                              <Row className="g-2 mb-3">
                                <Col md={6}>
                                  <Form.Control
                                    size="sm"
                                    placeholder="Название группы (напр. Соусы)"
                                    value={g.name}
                                    onChange={(e) => updateEditGroupField(editingItem._id, gidx, 'name', e.target.value)}
                                    disabled={!canEdit}
                                  />
                                </Col>
                                <Col md={3}>
                                  <Form.Select
                                    size="sm"
                                    value={g.type || 'multiple'}
                                    onChange={(e) => updateEditGroupField(editingItem._id, gidx, 'type', e.target.value)}
                                    disabled={!canEdit}
                                  >
                                    <option value="single">Один вариант</option>
                                    <option value="multiple">Несколько вариантов</option>
                                  </Form.Select>
                                </Col>
                                <Col md={3}>
                                  <Form.Control
                                    size="sm"
                                    type="number"
                                    min="1"
                                    placeholder="Макс. выбор"
                                    value={g.maxSelect || ''}
                                    onChange={(e) => updateEditGroupField(editingItem._id, gidx, 'maxSelect', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                                    disabled={!canEdit}
                                  />
                                </Col>
                              </Row>
                              
                              <div className="d-flex align-items-center gap-3 mb-3">
                                <Form.Check
                                  type="switch"
                                  id={`edit-greq-${editingItem._id}-${gidx}`}
                                  label="Обязательная группа"
                                  checked={!!g.required}
                                  onChange={(e) => updateEditGroupField(editingItem._id, gidx, 'required', e.target.checked)}
                                  disabled={!canEdit}
                                />
                              </div>
                              
                              <div>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <h6 className="mb-0">Опции в группе</h6>
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => {
                                      const list = (editItemMap[editingItem._id]?.addonGroups || []).slice();
                                      const opts = (list[gidx].options || []).slice();
                                      opts.push({ name: '', price: 0 });
                                      list[gidx] = { ...list[gidx], options: opts };
                                      onEditItemChange(editingItem._id, 'addonGroups', list);
                                    }}
                                    disabled={!canEdit}
                                  >
                                    ➕ Добавить опцию
                                  </Button>
                                </div>
                                
                                {(g.options || []).map((opt, oidx) => (
                                  <div key={oidx} className="card border-0 bg-light p-2 mb-2">
                                    <Row className="g-2 align-items-center">
                                      <Col md={6}>
                                        <Form.Control
                                          size="sm"
                                          placeholder="Название опции"
                                          value={opt.name}
                                          onChange={(e) => {
                                            const list = (editItemMap[editingItem._id]?.addonGroups || []).slice();
                                            const opts = (list[gidx].options || []).slice();
                                            opts[oidx] = { ...opts[oidx], name: e.target.value };
                                            list[gidx] = { ...list[gidx], options: opts };
                                            onEditItemChange(editingItem._id, 'addonGroups', list);
                                          }}
                                          disabled={!canEdit}
                                        />
                                      </Col>
                                      <Col md={4}>
                                        <Form.Control
                                          size="sm"
                                          type="number"
                                          step="0.01"
                                          placeholder="Цена (BYN)"
                                          value={opt.price}
                                          onChange={(e) => {
                                            const list = (editItemMap[editingItem._id]?.addonGroups || []).slice();
                                            const opts = (list[gidx].options || []).slice();
                                            opts[oidx] = { ...opts[oidx], price: parseFloat(e.target.value) || 0 };
                                            list[gidx] = { ...list[gidx], options: opts };
                                            onEditItemChange(editingItem._id, 'addonGroups', list);
                                          }}
                                          disabled={!canEdit}
                                        />
                                      </Col>
                                      <Col md={2} className="text-end">
                                        <Button
                                          size="sm"
                                          variant="outline-danger"
                                          onClick={() => {
                                            const list = (editItemMap[editingItem._id]?.addonGroups || []).slice();
                                            const opts = (list[gidx].options || []).slice();
                                            opts.splice(oidx, 1);
                                            list[gidx] = { ...list[gidx], options: opts };
                                            onEditItemChange(editingItem._id, 'addonGroups', list);
                                          }}
                                          disabled={!canEdit}
                                        >
                                          Удалить
                                        </Button>
                                      </Col>
                                    </Row>
                                  </div>
                                ))}
                                
                                {(!g.options || g.options.length === 0) && (
                                  <div className="text-center text-muted py-3">
                                    <span className="fs-5">📋</span>
                                    <p className="mb-0">Нет опций в группе</p>
                                    <small>Добавьте опции для этой группы</small>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          
                          {(!editItemMap[editingItem._id]?.addonGroups || editItemMap[editingItem._id].addonGroups.length === 0) && (
                            <div className="text-center text-muted py-4">
                              <span className="fs-4">📦</span>
                              <p className="mb-0">Пока нет групп дополнений</p>
                              <small>Нажмите "Добавить группу" чтобы создать первую группу</small>
                            </div>
                          )}
                        </div>
                      </Tab>
                    </Tabs>
                  )}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditModalVisible(false)}>
            Отмена
          </Button>
          <Button 
            variant="primary" 
            onClick={() => {
              if (editingItem) {
                saveEditItem(editingItem._id);
                setEditModalVisible(false);
              }
            }}
            disabled={!canEdit}
          >
            Сохранить
          </Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
};

export default MenuManagement;